import apiClient from './axiosClient';
import type {
  LoginRequest,
  LoginResponse,
  RefreshResponse,
  Tenant,
  TenantFormData,
  PlanTier,
  Channel,
  ChannelFormData,
  AnalyticsQuery,
  AnalyticsOverview,
  AnalyticsTimeseries,
  AnalyticsConsultations,
  AnalyticsQuestionsPage,
  AnalyticsCost,
  AnalyticsCostTimeseries,
  ModelPrice,
  UpsertModelPrice,
  AuditLogEntry,
  AuditLogFilter,
  PaginatedResponse,
  AgentConfig,
  AgentConfigFormData,
  BotEntity,
  BotEntityFormData,
  PlatformUser,
  CreateUserRequest,
  CreateUserResponse,
  UpdateRolesRequest,
  UpdateStatusRequest,
  ResetPasswordResponse,
  EmailIdentityMessageResponse,
  ValidateActionTokenResponse,
  TestSendEmailResponse,
  TestImapConnectionResponse,
  EmailConnectionTestResponse,
  WhatsAppProvisionResponse,
  WhatsAppChannelStatusResponse,
  TestSendWhatsAppResponse,
  WhatsAppConnection,
  WhatsAppConnectionSecretResponse,
  WhatsAppConnectionProvisionResponse,
  WhatsAppConnectionDisconnectResponse,
  WidgetCredentialRotationKind,
  RotateWidgetCredentialsResponse,
} from '@/types';

// ── Auth ───────────────────────────────────────────────────────

export const authApi = {
  login: (data: LoginRequest) =>
    apiClient.post<LoginResponse>('/api/identity/login', data),

  refresh: (refreshToken: string) =>
    apiClient.post<RefreshResponse>('/api/identity/refresh', { refreshToken }),

  /** Marketing-site sign-in hand-off, step 2 (PRE-auth): exchange the single-use code the marketing
   *  site's /login obtained for a normal session. `tenantId` is the canonical id the start step
   *  returned, sent as X-Tenant-Id because no stored user exists yet. The server reads this page's
   *  Origin to decide that only an Admin Portal code may be consumed here. Any failure is 400. */
  redeemHandoff: (code: string, tenantId: string) =>
    apiClient.post<LoginResponse>('/api/identity/handoff/redeem', { code }, { headers: { 'X-Tenant-Id': tenantId } }),

  logout: () =>
    apiClient.post('/api/identity/logout'),

  /** ADR-010 — password step-up. Re-verify the current user's password and receive a
   *  single-use, short-lived confirmation token for a sensitive action (default purpose:
   *  tenant erasure). Maps to POST /api/auth/reauth via the gateway. */
  reauth: (password: string, purpose = 'tenant_erasure') =>
    apiClient.post<{ token: string }>('/api/identity/reauth', { password, purpose }),
};

// ── Portal Users (RBAC admin platform) ─────────────────────────
//
// Gateway maps /api/identity/{**} -> IdentityAuthService /api/auth/{**}.
// These endpoints back the Users administration page (Wave 3b/3c). The list
// endpoint is paged + filterable; create returns the new user plus a one-time
// `tempPassword` (only when authMode==='tempPassword').

/** Paged shape returned by `GET /api/identity/users`. Distinct from the
 *  generic PaginatedResponse — this surface uses `total` (not `totalCount`)
 *  and carries no `totalPages`. */
export interface UserListResponse {
  items: PlatformUser[];
  page: number;
  pageSize: number;
  total: number;
}

/** Query params for `GET /api/identity/users`. All optional. */
export interface UserListParams {
  page?: number;
  pageSize?: number;
  /** Free-text search across email / displayName. */
  search?: string;
  /** Filter by main role (canonical name). */
  role?: string;
}

export const userApi = {
  /** List portal users, paged + filterable. */
  list: (params: UserListParams = {}) =>
    apiClient.get<UserListResponse>('/api/identity/users', { params }),

  /** Load a single portal user by id. */
  getById: (userId: string) =>
    apiClient.get<PlatformUser>(`/api/identity/users/${userId}`),

  /** Create a portal user. 201 → user (+ one-time `tempPassword` when
   *  authMode==='tempPassword').
   *
   *  `tenantId` targets a SPECIFIC tenant via the X-Tenant-Id header — used to
   *  create the initial admin for a just-created tenant (ADR-009 D7). A
   *  platformAdmin caller's header is honored by the backend ResolveTenantContext;
   *  the explicit header wins over the axios tenant interceptor. Omit it for the
   *  normal "current context" create. */
  create: (data: CreateUserRequest, tenantId?: string) =>
    apiClient.post<CreateUserResponse>(
      '/api/identity/users',
      data,
      tenantId ? { headers: { 'X-Tenant-Id': tenantId } } : undefined,
    ),

  /** Change a user's main role + developer overlay. */
  updateRoles: (userId: string, data: UpdateRolesRequest) =>
    apiClient.put<PlatformUser>(`/api/identity/users/${userId}/roles`, data),

  /** Activate / deactivate a user. */
  updateStatus: (userId: string, data: UpdateStatusRequest) =>
    apiClient.put<PlatformUser>(`/api/identity/users/${userId}/status`, data),

  /** Issue a fresh one-time temporary password for a user. */
  resetPassword: (userId: string) =>
    apiClient.post<ResetPasswordResponse>(`/api/identity/users/${userId}/reset-password`),

  /** Clear a lockout (failed attempts + lock window) without changing the password (OP-223). */
  unlock: (userId: string) =>
    apiClient.post<PlatformUser>(`/api/identity/users/${userId}/unlock`),

  /** Delete a portal user. 204 No Content. */
  remove: (userId: string) =>
    apiClient.delete(`/api/identity/users/${userId}`),

  /** Re-send the staff invitation / verification email to an unverified user
   *  (ADR-012). 200 → the (unchanged) user; 409 `{error:"already_verified"}`
   *  when the account has already completed verification. Gated by
   *  `users.manage` server-side (same policy as the rest of this object). */
  resendVerification: (userId: string) =>
    apiClient.post<PlatformUser>(`/api/identity/users/${userId}/resend-invite`),
};

// ── Email identity (ADR-012) ────────────────────────────────────
//
// Six ANONYMOUS (pre-auth) endpoints backing the /forgot-password,
// /reset-password, /verify-email and /set-password public pages, plus the
// "resend verification" affordance's non-consuming token check. Every call
// here runs before a session exists, so — exactly like `ssoApi.changePassword`
// — `tenantId` travels BOTH as the request body field AND as an explicit
// `X-Tenant-Id` header override (the axios tenant interceptor has no stored
// user/tenant to fall back to pre-auth). Gateway maps /api/identity/{**} ->
// IdentityAuthService /api/auth/{**}.
//
// forgot-password / resend-verification are intentionally NON-ENUMERATING —
// they always resolve 200 regardless of whether the account exists, so a
// caller must never infer account existence from the response.

export const emailAuthApi = {
  /** Always 200 (non-enumerating). 400 `{error:"tenantId and email are required"}`
   *  only on missing fields. */
  forgotPassword: (tenantId: string, email: string, language?: string) =>
    apiClient.post<EmailIdentityMessageResponse>(
      '/api/identity/forgot-password',
      { tenantId, email, language },
      { headers: { 'X-Tenant-Id': tenantId } },
    ),

  /** 200 on success. 400 `{error:"invalid_or_expired_token"}` | 400
   *  `{error:"weak_password", errors:[...]}`. */
  resetPassword: (tenantId: string, token: string, newPassword: string) =>
    apiClient.post<EmailIdentityMessageResponse>(
      '/api/identity/reset-password',
      { tenantId, token, newPassword },
      { headers: { 'X-Tenant-Id': tenantId } },
    ),

  /** 200 on success. 400 `{error:"invalid_code"}` (identical for an unknown
   *  user AND a wrong/expired code — non-enumerating). */
  verifyEmail: (tenantId: string, email: string, code: string) =>
    apiClient.post<EmailIdentityMessageResponse>(
      '/api/identity/verify-email',
      { tenantId, email, code },
      { headers: { 'X-Tenant-Id': tenantId } },
    ),

  /** Always 200 (non-enumerating). 400 `{error:"tenantId and email are required"}`
   *  only on missing fields. */
  resendVerification: (tenantId: string, email: string, language?: string) =>
    apiClient.post<EmailIdentityMessageResponse>(
      '/api/identity/resend-verification',
      { tenantId, email, language },
      { headers: { 'X-Tenant-Id': tenantId } },
    ),

  /** 200 on success. 400 `{error:"invalid_or_expired_token"}` | 400
   *  `{error:"weak_password", errors:[...]}`. */
  setInitialPassword: (tenantId: string, token: string, newPassword: string) =>
    apiClient.post<EmailIdentityMessageResponse>(
      '/api/identity/set-initial-password',
      { tenantId, token, newPassword },
      { headers: { 'X-Tenant-Id': tenantId } },
    ),

  /** Non-consuming check — safe to call before the user has typed anything.
   *  `type` must match the token's own purpose ('reset' for /reset-password,
   *  'setInitial' for /set-password); an unknown type resolves `{valid:false}`. */
  validateActionToken: (tenantId: string, token: string, type: 'reset' | 'setInitial') =>
    apiClient.post<ValidateActionTokenResponse>(
      '/api/identity/validate-action-token',
      { tenantId, token, type },
      { headers: { 'X-Tenant-Id': tenantId } },
    ),
};

// ── Agent Profiles (ADR-016 — per-agent concurrency cap) ────────
//
// HumanAgentHub owns per-agent operational state. The gateway maps
// /api/agent-profiles/{**}. GET returns the RESOLVED cap (0–10; an agent with
// no stored row gets the tenant/config default). PUT (this admin surface) is
// gated by `users.manage`; 0–10 is validated server-side (400 otherwise), so
// the client mirror below is a UX nicety, not the source of truth.

/** Shape of GET/PUT /api/agent-profiles/{agentId}. */
export interface AgentProfileResponse {
  agentId: string;
  /** Resolved per-agent concurrency cap, 0–10 (0 = route nothing here). */
  maxConcurrentConversations: number;
}

export const agentProfileApi = {
  /** Read an agent's resolved cap (prefill for the Human Agent Settings field). */
  getProfile: (agentId: string) =>
    apiClient.get<AgentProfileResponse>(`/api/agent-profiles/${agentId}`),

  /** Set an agent's cap (0–10). Gated by users.manage on the server. */
  updateProfile: (agentId: string, maxConcurrentConversations: number) =>
    apiClient.put<AgentProfileResponse>(`/api/agent-profiles/${agentId}`, {
      maxConcurrentConversations,
    }),
};

// ── Password change ─────────────────────────────────────────────
//
// Microsoft SSO removed 2026-06-09 (deferred). The `discovery` (GET /sso/config)
// and `exchange` (POST /agent/token) calls lived here — re-enable per
// Output/Runbooks/admin-portal-microsoft-sso-reenable.md. Only `changePassword`
// remains: it backs the forced first-login reset (ChangePasswordPage) and is NOT
// SSO-specific. The BACKEND request model (ChangePasswordRequest) uses the field
// names email / currentPassword / newPassword — matched exactly. (Group name kept
// as `ssoApi` so the change-password call site is untouched.)

export const ssoApi = {
  /** Rotate a password. Body field names mirror the backend ChangePasswordRequest
   *  exactly: email / currentPassword / newPassword. On success the backend returns
   *  a full AuthResponse (tokens), so the caller gets a session immediately.
   *
   *  `tenantId` is sent as the X-Tenant-Id header. It is REQUIRED for the forced
   *  first-login reset, which runs PRE-authentication (no stored user → the axios
   *  tenant interceptor can't supply the header, and TenantValidationMiddleware
   *  400s without it). Omit it only when an authenticated interceptor will add it. */
  changePassword: (email: string, currentPassword: string, newPassword: string, tenantId?: string) =>
    apiClient.post<LoginResponse>(
      '/api/identity/change-password',
      { email, currentPassword, newPassword },
      tenantId ? { headers: { 'X-Tenant-Id': tenantId } } : undefined,
    ),
};

// ── Tenants ────────────────────────────────────────────────────

/** Create-tenant request body. ADR-009 D3: a license with maxUsers >= 1 is REQUIRED,
 *  and contactName is required server-side; the backend reads `plan` (not `planTier`). */
export interface CreateTenantPayload {
  name: string;
  contactEmail: string;
  contactName: string;
  plan: string;
  maxAgents: number;
  maxChannelsPerBot: number;
  license: { maxUsers: number };
}

export const tenantApi = {
  // Backend GET /api/tenants returns a BARE array (List<TenantResponse>), and its
  // JSON fields are `id`/`plan` — NOT the `tenantId`/`planTier` the pages read.
  // Mirror the channelApi adapter: wrap the array into the PaginatedResponse
  // envelope AND normalize the field names. Without the wrap, `res.data.items` was
  // undefined → Header's `tenants.map()` threw "Cannot read properties of undefined
  // (reading 'map')", and because the Header renders OUTSIDE AppLayout's inner
  // ErrorBoundary it took down the entire page right after a platformAdmin login.
  // (2026-06-12 — fixes the admin@niloosoft.com login crash + the tenant switcher.)
  list: (page = 1, pageSize = 20) =>
    apiClient
      .get<Array<Tenant & { id?: string; plan?: string }>>('/api/tenants', {
        params: { page, pageSize },
      })
      .then((r) => {
        const raw = Array.isArray(r.data) ? r.data : [];
        const items: Tenant[] = raw.map((t) => ({
          ...t,
          tenantId: t.tenantId ?? t.id ?? '',
          planTier: (t.planTier ?? (t.plan as PlanTier)) as PlanTier,
        }));
        return {
          ...r,
          data: {
            items,
            totalCount: items.length,
            page,
            pageSize,
            totalPages: 1,
          } as PaginatedResponse<Tenant>,
        };
      }),

  getById: (tenantId: string) =>
    apiClient.get<Tenant>(`/api/tenants/${tenantId}`),

  /**
   * OP-838 — the minimal tenant read: id and display name, nothing else.
   *
   * `getById` above is now gated on `tenants.manage`, which is platformAdmin-only, because the full
   * record carries the tenant's billing contact and billing-system identifiers. `useTenantName`
   * needed one string and was fetching all of that to get it, from four TENANT-admin screens that
   * would have started 403ing the moment the gate landed.
   *
   * Use this for anything that only needs to DISPLAY the tenant's name. Reach for `getById` only
   * when you genuinely need the record, and expect it to require platformAdmin.
   */
  getNameById: (tenantId: string) =>
    apiClient.get<{ id: string; name: string }>(`/api/tenants/${tenantId}/name`),

  /**
   * OP-838 — the tenant's own profile: everything the Settings → Profile tab shows, and no billing
   * identifiers. Gated on `config.edit`, the same permission the `/settings` route already requires.
   *
   * ADR-009 makes the profile platform-admin-MANAGED, not platform-admin-VISIBLE — a tenant admin
   * views their profile and limits and can never save them. That read lives here now, so `getById`
   * could be gated to platformAdmin without taking it away.
   */
  getProfile: (tenantId: string) =>
    apiClient.get<Tenant>(`/api/tenants/${tenantId}/profile`),

  create: (data: CreateTenantPayload) =>
    apiClient.post<Tenant>('/api/tenants', data),

  update: (tenantId: string, data: TenantFormData) =>
    apiClient.put<Tenant>(`/api/tenants/${tenantId}`, data),

  suspend: (tenantId: string, reason: string) =>
    apiClient.post(`/api/tenants/${tenantId}/suspend`, { reason }),

  reactivate: (tenantId: string) =>
    apiClient.post(`/api/tenants/${tenantId}/reactivate`),

  /** ADR-010 / OP-205 — PERMANENT cross-service erasure. Irreversible. Requires the
   *  single-use step-up token from authApi.reauth, sent as the X-Reauth-Token header.
   *  Returns the per-service purge summary (200), or a partial-failure summary (207).
   *  OP-330 — this is a synchronous fan-out across ~19 services; the apiClient-wide
   *  30s default aborted the client request while the purge kept running server-side
   *  (and the single-use reauth token was already spent by then). */
  erase: (tenantId: string, reauthToken: string) =>
    apiClient.post<TenantEraseSummary>(
      `/api/tenants/${tenantId}/erase`,
      {},
      { headers: { 'X-Reauth-Token': reauthToken }, timeout: 5 * 60 * 1000 },
    ),
};

/** Response shape of POST /api/tenants/{id}/erase. */
export interface TenantEraseSummary {
  tenantId: string;
  erased: boolean;
  totalDocumentsPurged: number;
  tenantRowDeleted: boolean;
  perService: Array<{ target: string; ok: boolean; documentsPurged: number; error?: string | null }>;
}

// ── Bots ───────────────────────────────────────────────────────
// OP-559: `botApi` is REMOVED. It targeted /api/bots, which has no gateway route (OP-164) and no
// backend controller, so every call it made 404'd; it survived only because two unrouted pages
// (BotListPage / BotFormPage, also removed) still imported it. The agent-to-channel binding the
// product actually uses is `botEntityApi` below — "Bot Attachments" in the UI.

// ── Channels ───────────────────────────────────────────────────
// Note: channels are managed by TenantManagementService as ChannelConfig entities.
// The /api/channels path on ApiGateway → ChannelAdapterService (webhook handler, not CRUD).
// Admin CRUD uses /api/tenants/{tenantId}/channel-configs → TenantManagementService.

import { useAuthStore } from '@/stores/authStore';
import { useTenantStore } from '@/stores/tenantStore';
import type { BlockedSender, RegenerateSecretResponse, WidgetEmbedInfo } from '@/types';

// OP-210 — resolve the tenant for channel-config URLs the SAME way the axios
// Tenant-Id interceptor (axiosClient.ts:49-52) and useEffectiveTenantId do: a
// platformAdmin operates on the tenant SELECTED in the Header picker (impersonation),
// NOT their home platform tenant. Returning user.tenantId here meant every channelApi
// call hit /api/tenants/PlatformSystemAdmin/channel-configs while the page created bots
// under the selected tenant — so the Channel dropdown showed the wrong tenant's channels
// and "Create Bot" failed with "ChannelConfig '…' not found for tenant '…'". This was the
// one endpoint missed by the 13-page useEffectiveTenantId impersonation fix (commit 1e3fa9e).
function getTenantId(): string {
  const user = useAuthStore.getState().user;
  if (user?.role === 'PlatformAdmin') {
    return useTenantStore.getState().selectedTenantId ?? user.tenantId ?? '';
  }
  return user?.tenantId ?? '';
}

export const channelApi = {
  list: (_page = 1, _pageSize = 20) =>
    apiClient.get<Channel[]>(`/api/tenants/${getTenantId()}/channel-configs`)
      .then((r) => ({
        ...r,
        data: { items: r.data, totalCount: r.data.length, page: 1, pageSize: r.data.length, totalPages: 1 }
      })),

  getById: (channelId: string) =>
    apiClient.get<Channel>(`/api/tenants/${getTenantId()}/channel-configs/${channelId}`),

  /**
   * The hosts and URLs a tenant's developer needs, resolved by the SERVER from the config plane.
   *
   * Route is `channel-configs/embed-info` rather than a second route root; the literal segment
   * outranks `{id}` in ASP.NET endpoint routing, so it cannot be swallowed by `getById`.
   *
   * WHY THIS IS NOT DERIVED IN THE BROWSER: it used to be, from the build-time `VITE_API_BASE_URL`
   * and `VITE_WIDGET_BASE_URL`. Those are empty in the shipped images — `admin-portal:v34` was built
   * with `VITE_API_BASE_URL=` — so the relay snippet this portal showed a tenant read
   * `https://YOUR_API_HOST/api/widget/embed/v1/bootstrap.js`. The portal's own `VITE_API_BASE_URL`
   * is correctly empty (it proxies `/api` same-origin through nginx), so the two uses were in direct
   * conflict and no build value could satisfy both. The server reads these same keys to build the
   * bootstrap it serves, so asking it is the only way the two cannot drift.
   */
  getWidgetEmbedInfo: () =>
    apiClient.get<WidgetEmbedInfo>(`/api/tenants/${getTenantId()}/channel-configs/embed-info`),

  create: (data: ChannelFormData) =>
    apiClient.post<Channel>(`/api/tenants/${getTenantId()}/channel-configs`, data),

  /**
   * PARTIAL by design (OP-166). `UpdateChannelConfigRequest` declares every field nullable with no
   * `[Required]`, and `ChannelConfigService.UpdateAsync` applies each ONLY when non-null — so a body
   * carrying one key changes exactly that key. Typing this as the full `ChannelFormData` forced a
   * caller either to round-trip fields it never touched (the shape that quietly rewrites something
   * nobody looked at) or to cast, which hides the question. `Partial` is what the server actually
   * accepts.
   */
  update: (channelId: string, data: Partial<ChannelFormData>) =>
    apiClient.put<Channel>(`/api/tenants/${getTenantId()}/channel-configs/${channelId}`, data),

  /**
   * Enable or disable a channel (OP-534) — the operator kill switch.
   *
   * The backend has always accepted `isEnabled` on the update request and both inbound email
   * pollers have always honoured it, but NOTHING in the product ever wrote it: `ChannelFormData`
   * has no such field, so neither create nor update could transmit it, and the list rendered it
   * as a read-only Yes/No. Disabling a channel required a hand-rolled HTTP call or a Mongo edit.
   * That is why, on 2026-08-09, stopping one runaway mailbox meant stopping 25 containers.
   *
   * Sent as a dedicated single-field PATCH-shaped update rather than through `ChannelFormData`,
   * so turning a channel off can never carry unrelated form state with it.
   */
  setEnabled: (channelId: string, isEnabled: boolean) =>
    apiClient.put<Channel>(
      `/api/tenants/${getTenantId()}/channel-configs/${channelId}`,
      { isEnabled },
    ),

  delete: (channelId: string) =>
    apiClient.delete(`/api/tenants/${getTenantId()}/channel-configs/${channelId}`),

  /**
   * Regenerate the channel secret for a WebChat channel.
   * The new raw secret is returned ONCE in `channelSecret`; the caller must
   * display it to the operator immediately — it cannot be retrieved again.
   *
   * Backend route: POST /api/tenants/{tenantId}/channel-configs/{id}/regenerate-secret
   * (ChannelConfigController.RegenerateSecret, line ~395 in the controller)
   */
  regenerateSecret: (channelId: string) =>
    apiClient.post<RegenerateSecretResponse>(
      `/api/tenants/${getTenantId()}/channel-configs/${channelId}/regenerate-secret`,
    ),

  /**
   * ADR-022 §5.2 — rotates one or more widget-embed handshake credential artifacts (the Widget
   * Identifier, the Secret Key, and/or the Secret Value/domain-list hash). A rotated Secret Key
   * is returned ONCE in `secretKey` — display it immediately, it cannot be retrieved again.
   * Backend route: POST .../channel-configs/{id}/widget-credentials/rotate
   * (ChannelConfigController.RotateWidgetCredentials).
   */
  rotateWidgetCredentials: (channelId: string, kinds: WidgetCredentialRotationKind[], graceMinutes?: number) =>
    apiClient.post<RotateWidgetCredentialsResponse>(
      `/api/tenants/${getTenantId()}/channel-configs/${channelId}/widget-credentials/rotate`,
      { kinds, ...(graceMinutes !== undefined ? { graceMinutes } : {}) },
    ),

  /**
   * Sends a one-off test email through this Email channel's configured outbound SMTP
   * credentials. Backend route: POST .../channel-configs/{id}/test-send-email
   * (ChannelConfigController.TestSendEmail).
   */
  testSendEmail: (channelId: string, recipientEmail: string) =>
    apiClient.post<TestSendEmailResponse>(
      `/api/tenants/${getTenantId()}/channel-configs/${channelId}/test-send-email`,
      { recipientEmail },
    ),

  /**
   * Connects to this Email channel's configured inbound IMAP mailbox,
   * authenticates, reads mailbox status, and disconnects — the twin of
   * testSendEmail above (ADR-021 D7). Backend route: POST
   * .../channel-configs/{id}/test-imap-connection (empty body).
   */
  /**
   * ADR-021 A2 - starts the delegated Microsoft consent flow. Returns the Microsoft sign-in URL to
   * send the operator's browser to; it holds no secret and changes nothing on its own.
   */
  startMicrosoftConnect: (channelId: string) =>
    apiClient.post<{ authorizeUrl: string }>(
      `/api/tenants/${getTenantId()}/channel-configs/${channelId}/oauth/microsoft/connect`,
      {},
    ),

  testImapConnection: (channelId: string) =>
    apiClient.post<TestImapConnectionResponse>(
      `/api/tenants/${getTenantId()}/channel-configs/${channelId}/test-imap-connection`,
      {},
    ),

  /**
   * Combined "Test Connection" for the unified Microsoft 365 OAuth connection method — verifies
   * BOTH IMAP and SMTP in one click, since one OAuth consent now authorizes both directions.
   * Backend route: POST .../channel-configs/{id}/test-email-connection (empty body).
   */
  testEmailConnection: (channelId: string) =>
    apiClient.post<EmailConnectionTestResponse>(
      `/api/tenants/${getTenantId()}/channel-configs/${channelId}/test-email-connection`,
      {},
    ),

  // ── Blocked senders (email channels) ──────────────────────────────────────
  // Routed through the gateway like the WhatsApp tool below (/api/channels/**). The path carries NO
  // tenant segment on purpose: the tenant is the caller's JWT `tenantId` claim, never a route value
  // (ADR-008), and the block list is keyed by tenant + channel on the server. Both calls require the
  // `bots.manage` policy; the portal gate that hides the section is UX, the server is the authority.

  /**
   * The senders staff blocked on this channel from the agent console's spam action.
   * Backend route: GET /api/channels/{channelConfigId}/blocked-senders -> 200 BlockedSender[].
   */
  listBlockedSenders: (channelId: string) =>
    apiClient.get<BlockedSender[]>(`/api/channels/${encodeURIComponent(channelId)}/blocked-senders`),

  /**
   * Lifts one block. From the channel's next mailbox poll, mail from that sender is read again.
   * Backend route: DELETE /api/channels/{channelConfigId}/blocked-senders/{id} -> 204, or 404
   * `{ error }` when the block no longer exists.
   */
  removeBlockedSender: (channelId: string, blockedSenderId: string) =>
    apiClient.delete(
      `/api/channels/${encodeURIComponent(channelId)}/blocked-senders/${encodeURIComponent(blockedSenderId)}`,
    ),

  // ── WhatsApp Configuration Tool (ADR-020 D9) ─────────────────────────────
  // These target ChannelAdapterService (gateway route /api/channels/** →
  // channel-adapter-cluster), NOT TenantManagement — provisioning is a
  // provider conversation, storage stays with the channel-config CRUD above.

  /**
   * Provision the channel at 360Dialog: mints a fresh webhook secret, registers our
   * webhook URL + secret header with the tenant's D360 API key, writes status back.
   * Backend route: POST /api/channels/whatsapp/{id}/provision (WhatsAppChannelAdminController).
   */
  provisionWhatsApp: (channelId: string) =>
    apiClient.post<WhatsAppProvisionResponse>(`/api/channels/whatsapp/${channelId}/provision`, {}),

  /**
   * Combined local + provider status: reads the webhook registered at 360Dialog and
   * compares it to the expected URL. Backend route: GET /api/channels/whatsapp/{id}/status.
   */
  whatsAppStatus: (channelId: string) =>
    apiClient.get<WhatsAppChannelStatusResponse>(`/api/channels/whatsapp/${channelId}/status`),

  /**
   * Send one operator test message through the channel. NOTE: outside the recipient's own
   * 24h window Meta rejects free-form sends (error 131047) — message the business number
   * first, then test-send back. Backend route: POST /api/channels/whatsapp/{id}/test-send.
   */
  testSendWhatsApp: (channelId: string, to: string, text?: string) =>
    apiClient.post<TestSendWhatsAppResponse>(`/api/channels/whatsapp/${channelId}/test-send`, { to, text }),
};

/**
 * ADR-020 Phase 2 — the tenant's 360Dialog accounts (WABAs) and the shared inbound webhook each
 * one owns. Backed by TenantManagementService's `WhatsAppConnectionsController`
 * (`/api/tenants/{tenantId}/whatsapp-connections`), the same tenant-scoped shape channelApi uses.
 *
 * Deliberately a separate object rather than more methods on `channelApi`: a connection is not a
 * channel — several channels attach to one, and it is created and deleted on different occasions.
 */
export const whatsAppConnectionApi = {
  list: () =>
    apiClient.get<WhatsAppConnection[]>(`/api/tenants/${getTenantId()}/whatsapp-connections`),

  /**
   * Creates a connection and mints its webhook secret. The raw secret is in THIS response only —
   * we store just its hash, so the caller must show it before discarding the response.
   */
  create: (name: string) =>
    apiClient.post<WhatsAppConnectionSecretResponse>(
      `/api/tenants/${getTenantId()}/whatsapp-connections`,
      { name },
    ),

  /**
   * OP-628 — registers this connection's shared webhook at 360Dialog for every attached number,
   * replacing the manual Hub copy-paste.
   *
   * ALWAYS rotates the secret (only its hash is stored, so there is nothing else to push), and the
   * response carries the new one ONCE. A partial run answers 200 with per-number `results` and a
   * `warning` — some numbers really did register, and the operator must see which did not.
   *
   * Backend route: POST /api/tenants/{tenantId}/whatsapp-connections/{id}/provision.
   * 409 when the platform's public webhook base URL is unset.
   */
  provision: (connectionId: string) =>
    apiClient.post<WhatsAppConnectionProvisionResponse>(
      `/api/tenants/${getTenantId()}/whatsapp-connections/${connectionId}/provision`,
    ),

  /**
   * OP-629 — clears this connection's webhook from every attached number. The inverse of
   * {@link provision}, and the only clean way to retire a WABA.
   *
   * Deletes nothing: connection, channels and secret all survive, so `provision` puts it straight
   * back. It MUST run while the numbers are still attached — their API keys are the only thing that
   * can clear their registrations, and deleting a channel (or the connection, which requires the
   * channels to be gone first) takes those keys away.
   *
   * Backend route: POST /api/tenants/{tenantId}/whatsapp-connections/{id}/disconnect.
   */
  disconnect: (connectionId: string) =>
    apiClient.post<WhatsAppConnectionDisconnectResponse>(
      `/api/tenants/${getTenantId()}/whatsapp-connections/${connectionId}/disconnect`,
    ),

  /**
   * Mints a REPLACEMENT secret WITHOUT pushing it anywhere — the manual path, for a webhook the
   * operator registered in the Hub by hand. Inbound stops for every number on the connection until
   * the new value is pasted in. Prefer `provision`, which rotates AND registers. Confirm first.
   */
  rotateSecret: (connectionId: string) =>
    apiClient.post<WhatsAppConnectionSecretResponse>(
      `/api/tenants/${getTenantId()}/whatsapp-connections/${connectionId}/rotate-secret`,
    ),

  /** 409 when numbers still route through it — the server refuses to orphan them. */
  delete: (connectionId: string) =>
    apiClient.delete(`/api/tenants/${getTenantId()}/whatsapp-connections/${connectionId}`),
};

// ── Configuration ──────────────────────────────────────────────
// Rewired 2026-07-12 (OP-325): configApi used to target /api/configuration*,
// which has no gateway route and no backend controller — every platformAdmin
// click 404'd. Real admin surface is ConfigurationManagementService's
// raw-entry API (same one pipelineConfigApi below already uses). There is no
// backend "deactivate"/soft-delete concept (ConfigEntryResponse has no
// isActive field) — only List + Create are wired for now.

/** Raw config entry shape from `GET /api/config/all` (admin endpoint). */
export interface RawConfigEntry {
  id: string;
  key: string;
  value: string;
  scope: string;          // 'global' | 'service' | 'tenant' | 'service_tenant'
  serviceName: string | null;
  tenantId: string | null;
  isEncrypted: boolean;
  description?: string | null;
  version: number;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

/** Paged shape returned by `GET /api/config/all`. */
export interface RawConfigPage {
  items: RawConfigEntry[];
  page: number;
  pageSize: number;
  totalCount: number;
}

/** Request body for `POST /api/config/entries` — matches CreateConfigEntryRequest. */
export interface CreateRawConfigEntryRequest {
  key: string;
  value: string;
  scope: string;           // 'global' | 'service' | 'tenant' | 'service_tenant'
  serviceName?: string | null;
  tenantId?: string | null;
  description?: string | null;
  isEncrypted?: boolean;
}

export const configApi = {
  list: (page = 1, pageSize = 20) =>
    apiClient.get<RawConfigPage>('/api/config/all', {
      params: { page, pageSize },
    }),

  create: (data: CreateRawConfigEntryRequest) =>
    apiClient.post<RawConfigEntry>('/api/config/entries', data),

  /**
   * Updates a raw config entry's value (OP-577).
   *
   * `PUT /api/config/entries/{id}` has always existed and is gated by `config.edit`. Its only
   * caller was `pipelineConfigApi.setValue`, a wrapper written for one specific pipeline key — so
   * the general Configuration page could LIST live values and change none of them. A platformAdmin
   * looking at `Email.Inbound.Graph.Enabled` on a live deployment could see it was `true` and had
   * no way in the product to make it `false`.
   *
   * Publishes `ConfigUpdated`, which since OP-540 actually reaches a receive endpoint, so services
   * pick the change up in about a second rather than waiting out the 5-minute poll.
   */
  setValue: (id: string, value: string, description?: string | null) =>
    apiClient.put<RawConfigEntry>(`/api/config/entries/${id}`, { value, description }),
};

// ── Pipeline tunables ─────────────────────────────────────────
// Thin wrapper over ConfigMgmt's raw-entry surface (admin-only) for
// reading + writing the live `AiAgent.Pipeline.EvalCertaintyThresholdPercent`
// row. Used by the Agent edit page so an admin can shift the bot's
// confidence-to-respond threshold without ssh / mongosh. Other pipeline
// knobs can be added here as more sliders are surfaced.

export const pipelineConfigApi = {
  /**
   * Generic raw-entry lookup by key (service-scoped to AiAgentService).
   * Returns null on any failure — auth, missing row, network — and
   * suppresses the global 401 interceptor's refresh-then-logout branch
   * (`_retry: true`) so an admin opening the agent page can never get
   * bounced to /login because of an optional config-sidebar fetch.
   *
   * Pulls page 1 at pageSize=500 (≈80 entries today, plenty of headroom).
   * When we outgrow this we'll add a dedicated `entries/by-key/{key}`
   * surface on ConfigMgmt.
   */
  getEntry: async (key: string): Promise<RawConfigEntry | null> => {
    try {
      const { data } = await apiClient.get<RawConfigPage>('/api/config/all', {
        params: { page: 1, pageSize: 1000 },
        _retry: true,
      } as any);
      const items = data.items ?? [];
      return items.find(
        (e) =>
          e.key === key &&
          e.scope === 'service' &&
          e.serviceName === 'AiAgentService',
      ) ?? null;
    } catch {
      return null;
    }
  },

  /**
   * Updates a raw config entry by id. Body matches ConfigMgmt's
   * `UpdateConfigEntryRequest` (`{ value, description? }`). Triggers a
   * `ConfigUpdated` MassTransit event on the backend so every service's
   * ConfigurationHelper refreshes within seconds. Same auth-failure
   * isolation as `getEntry`.
   */
  setValue: (id: string, value: string, description?: string | null) =>
    apiClient.put<RawConfigEntry>(
      `/api/config/entries/${id}`,
      { value, description },
      { _retry: true } as any,
    ),

  // Legacy aliases — keep callers compiling. Newer code should use
  // getEntry / setValue directly.
  getThreshold: () => pipelineConfigApi.getEntry('AiAgent.Pipeline.EvalCertaintyThresholdPercent'),
  setThresholdValue: (id: string, value: string, description?: string | null) =>
    pipelineConfigApi.setValue(id, value, description),
};

// ── Analytics ──────────────────────────────────────────────────

export const analyticsApi = {
  overview: (q: AnalyticsQuery) => apiClient.get<AnalyticsOverview>('/api/analytics/overview', { params: q }),
  timeseries: (q: AnalyticsQuery & { metric: string; granularity: 'day' | 'hour' }) =>
    apiClient.get<AnalyticsTimeseries>('/api/analytics/timeseries', { params: q }),
  consultations: (q: AnalyticsQuery) => apiClient.get<AnalyticsConsultations>('/api/analytics/consultations', { params: q }),
  questions: (q: AnalyticsQuery & { reason?: string; page?: number; pageSize?: number }) =>
    apiClient.get<AnalyticsQuestionsPage>('/api/analytics/questions', { params: q }),
  cost: (q: AnalyticsQuery & { groupBy: 'tenant' | 'area' | 'step' | 'model' | 'category'; allTenants?: boolean; category?: string }) =>
    apiClient.get<AnalyticsCost>('/api/analytics/cost', { params: q }),
  costTimeseries: (q: AnalyticsQuery & { granularity: 'day' | 'week' | 'month'; series: 'category' | 'tenant' | 'model'; allTenants?: boolean; category?: string }) =>
    apiClient.get<AnalyticsCostTimeseries>('/api/analytics/cost/timeseries', { params: q }),
  prices: () => apiClient.get<ModelPrice[]>('/api/analytics/prices'),
  upsertPrice: (p: UpsertModelPrice) => apiClient.post<ModelPrice>('/api/analytics/prices', p),
  deletePrice: (id: string) => apiClient.delete(`/api/analytics/prices/${encodeURIComponent(id)}`),
};

// ── Audit Log ──────────────────────────────────────────────────

export const auditApi = {
  // Backend route is /audit/search (mounted under /api/audit/ via the
  // gateway), NOT /audit/logs. Pre-fix the frontend was 404'ing every
  // request and surfacing "Failed to load audit logs.".
  search: (filter: AuditLogFilter) =>
    apiClient.get<AuditSearchResponse>('/api/audit/search', {
      params: filter,
    }),
};

/**
 * Shape returned by the AuditCompliance service's <c>GET /audit/search</c>.
 * Differs from the generic <c>PaginatedResponse&lt;T&gt;</c> we use for
 * other endpoints — Audit's response has <c>entries</c> + <c>total</c>
 * instead of <c>items</c> + <c>totalCount</c>. The page maps both shapes
 * to the table rows.
 */
export interface AuditSearchResponse {
  entries: AuditLogEntry[];
  totalCount: number;
  page: number;
  pageSize: number;
}

// ── Agent Configs ─────────────────────────────────────────────

export const agentConfigApi = {
  list: (tenantId: string) =>
    apiClient.get<AgentConfig[]>(`/api/tenants/${tenantId}/agent-configs`),

  getById: (tenantId: string, id: string) =>
    apiClient.get<AgentConfig>(`/api/tenants/${tenantId}/agent-configs/${id}`),

  create: (tenantId: string, data: AgentConfigFormData) =>
    apiClient.post<AgentConfig>(`/api/tenants/${tenantId}/agent-configs`, data),

  update: (tenantId: string, id: string, data: Partial<AgentConfigFormData>) =>
    apiClient.put<AgentConfig>(`/api/tenants/${tenantId}/agent-configs/${id}`, data),

  delete: (tenantId: string, id: string) =>
    apiClient.delete(`/api/tenants/${tenantId}/agent-configs/${id}`),
};

// ── Agent Knowledge (per-agent RAG sources) ───────────────────
// Endpoints below mirror the existing agent-config CRUD pattern. They are
// implemented in TenantManagementService alongside the AgentConfigs.
//
// Backend status: Phase B will land the controller + storage + indexing.
// The chat-widget will keep working today even if these endpoints return
// 404 — agent creation succeeds before any knowledge is uploaded.

import type {
  KnowledgeSource,
  ChunkPreview,
  TenantMind,
  TenantMindFormData,
  ChatAppearance,
  AgentBrain,
  AgentBrainFormData,
  AgentCore,
  AgentMemory,
  AgentFeedback,
  ConversationFeedback,
} from '@/types';

export const agentKnowledgeApi = {
  /** List the knowledge sources attached to an agent. */
  list: (tenantId: string, agentId: string) =>
    apiClient.get<KnowledgeSource[]>(
      `/api/tenants/${tenantId}/agent-configs/${agentId}/knowledge`
    ),

  /** Add a URL — server fetches, extracts, chunks, embeds, indexes. Returns
   *  the new source row in `pending` state; client polls or refreshes the
   *  list to see it transition to `indexed`. */
  addUrl: (tenantId: string, agentId: string, url: string) =>
    apiClient.post<KnowledgeSource>(
      `/api/tenants/${tenantId}/agent-configs/${agentId}/knowledge/urls`,
      { url }
    ),

  /** Upload a file (multipart/form-data, field name `file`). Same async
   *  ingestion contract as addUrl. Server now returns 201 immediately
   *  with status=Processing and runs parse+embed in the background, so
   *  this request only needs to outlast the bytes-on-the-wire phase.
   *  The 5-minute cap protects against pathological networks while
   *  still being far longer than any healthy upload takes. */
  uploadFile: (tenantId: string, agentId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post<KnowledgeSource>(
      `/api/tenants/${tenantId}/agent-configs/${agentId}/knowledge/files`,
      fd,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        // Overrides the apiClient-wide 30 s default. Pre-fix, the
        // server ran extract+parse+embed on the request thread and
        // the 30 s axios cap would canceled them all (audit log
        // "Enrichment SLM call failed: A task was canceled").
        timeout: 5 * 60 * 1000,
      }
    );
  },

  /** Remove a source AND all its chunks/embeddings. */
  remove: (tenantId: string, agentId: string, sourceId: string) =>
    apiClient.delete(
      `/api/tenants/${tenantId}/agent-configs/${agentId}/knowledge/${sourceId}`
    ),

  /** Toggle a source enabled/disabled. Disabled sources are skipped by
   *  retrieval. The flag mirrors onto every chunk so the retrieval hot
   *  path stays a single Mongo scan. */
  setEnabled: (tenantId: string, agentId: string, sourceId: string, enabled: boolean) =>
    apiClient.patch<KnowledgeSource>(
      `/api/tenants/${tenantId}/agent-configs/${agentId}/knowledge/${sourceId}/enabled`,
      { enabled }
    ),

  /** List the chunks produced from a single source. The 3072-dim embedding
   *  vector is omitted server-side to keep the payload small. */
  listChunks: (tenantId: string, agentId: string, sourceId: string) =>
    apiClient.get<ChunkPreview[]>(
      `/api/tenants/${tenantId}/agent-configs/${agentId}/knowledge/${sourceId}/chunks`
    ),
};

// ── Phase C — Agent Memory tiers ──────────────────────────────
//
// Tenant Mind and Agent Brain are HAND-AUTHORED tiers; the admin portal
// edits them via these endpoints. The other two tiers (Core, Memory) are
// machine-curated and currently surfaced read-only via the same TenantMgmt
// API once the observability views land in the next pass.
//
// Both endpoints return 404 when the row hasn't been authored yet. The
// frontend pages catch the 404 and render an empty form so the admin can
// seed the tier on first save.

export const tenantMindApi = {
  /** Load the tenant's hand-authored Mind. 404 → empty form. */
  get: (tenantId: string) =>
    apiClient.get<TenantMind>(`/api/tenants/${tenantId}/mind`),

  /** Upsert the tenant's Mind. PATCH-style — null fields preserved. */
  upsert: (tenantId: string, data: TenantMindFormData) =>
    apiClient.put<TenantMind>(`/api/tenants/${tenantId}/mind`, data),
};

// ── Chat Appearance (embedded chat-widget theme) ──────────────
//
// The chat-widget look (accent colour, dark mode, direction, language tag,
// header copy, greeting, logo initials) lives on TenantManagementService
// (5018) at /api/tenants/{t}/chat-appearance — NOT on the AgentMemory Mind
// endpoint. The GET never 404s — it returns Constellation defaults when the
// tenant has not customised anything. The PUT requires the `BotsManage`
// permission server-side and takes the FULL ChatAppearance body.
//
// (Previously the Tenant Mind page tried to round-trip chatAppearance inside
// the mind PUT; AgentMemoryService silently ignored it, so the theme never
// persisted. Moved to its own surface under the tenant Settings area.)
export const chatAppearanceApi = {
  get: (tenantId: string) =>
    apiClient.get<ChatAppearance>(`/api/tenants/${tenantId}/chat-appearance`),
  upsert: (tenantId: string, data: Partial<ChatAppearance>) =>
    apiClient.put<ChatAppearance>(`/api/tenants/${tenantId}/chat-appearance`, data),
};

export const agentBrainApi = {
  /** Load an agent's Brain. 404 → empty form on the Agent edit page. */
  get: (tenantId: string, agentId: string) =>
    apiClient.get<AgentBrain>(`/api/tenants/${tenantId}/agent-configs/${agentId}/brain`),

  /** Upsert an agent's Brain. Server verifies the agent exists for this tenant. */
  upsert: (tenantId: string, agentId: string, data: AgentBrainFormData) =>
    apiClient.put<AgentBrain>(`/api/tenants/${tenantId}/agent-configs/${agentId}/brain`, data),
};

// Read-only observability — Core, Memory, Feedback are machine-curated
// (Core via nightly promotion; Memory by the per-turn writer; Feedback by
// the chat widget). Admin can audit but not edit. Server returns 200 with
// an empty document on first read of Core (no nightly has run yet).

export const agentObservabilityApi = {
  getCore: (tenantId: string, agentId: string) =>
    apiClient.get<AgentCore>(`/api/tenants/${tenantId}/agent-configs/${agentId}/core`),

  listMemory: (tenantId: string, agentId: string, limit = 100) =>
    apiClient.get<AgentMemory[]>(
      `/api/tenants/${tenantId}/agent-configs/${agentId}/memory?limit=${limit}`,
    ),

  listFeedback: (tenantId: string, agentId: string, limit = 100) =>
    apiClient.get<AgentFeedback[]>(
      `/api/tenants/${tenantId}/agent-configs/${agentId}/feedback?limit=${limit}`,
    ),

  /**
   * List whole-conversation CSAT rows for one agent. Returns the most-recent
   * `limit` rows ordered by createdAt descending (default 100, max 500).
   *
   * Backend route: GET /api/tenants/{tenantId}/agent-configs/{agentId}/feedback/conversation
   * Hosted in TenantManagementService (5018). Path uses the same
   * `…/feedback/…` sub-tree as the per-message feedback so both surfaces
   * sit under the same agent-config scope. Auth: [Authorize] + tenant-validation
   * middleware; the admin portal's JWT carries the admin tenantId claim.
   */
  listConversationFeedback: (tenantId: string, agentId: string, limit = 100) =>
    apiClient.get<ConversationFeedback[]>(
      `/api/tenants/${tenantId}/conversation-feedback?agentId=${encodeURIComponent(agentId)}&limit=${limit}`,
    ),
};

// ── Learned-behavior proposals (review gate) ──────────────────
//
// The learning system writes learned BEHAVIOR as review-gated proposals:
// MindNotes prefixed `[proposed:{form}]` (form ∈ method | limitation |
// escalationRule | tenantVoice | generalNote) inside AgentCore.notes
// (agent scope) and TenantMind.notes (tenant scope). Auto-elevation to
// Brain was retired — an admin ADOPTs (promotes into Brain/TenantMind)
// or DISMISSes (discards) each proposal here.
//
// IMPORTANT: `proposalText` is the FULL note text INCLUDING the
// `[proposed:{form}]` prefix — the backend matches the note by exact
// text. Strip the prefix only for display, never for the payload.
//
// tenantScope=false → proposal lives in AgentCore; adopt returns the
// updated AgentBrain, dismiss returns the updated AgentCore.
// tenantScope=true  → proposal lives in TenantMind; both return TenantMind.

export interface ProposalActionRequest {
  /** Full proposal note text, including the `[proposed:{form}]` prefix. */
  proposalText: string;
  /** Behavior form parsed from the prefix; backend re-parses if empty. */
  form: string;
  /** false = AgentCore-scoped; true = TenantMind-scoped. */
  tenantScope: boolean;
}

export const agentProposalApi = {
  /**
   * Adopt a learned-behavior proposal: promotes it into the durable Brain
   * (Core scope) or TenantMind (tenant scope) and removes the proposal note.
   * Returns the updated AgentBrain when `tenantScope=false`, else TenantMind.
   */
  adopt: (
    tenantId: string,
    agentId: string,
    proposalText: string,
    form: string,
    tenantScope = false,
  ) =>
    apiClient.post<AgentBrain | TenantMind>(
      `/api/tenants/${tenantId}/agent-configs/${agentId}/core/proposals/adopt`,
      { proposalText, form, tenantScope } satisfies ProposalActionRequest,
    ),

  /**
   * Dismiss a learned-behavior proposal: removes the `[proposed:…]` note
   * without any durable write. Returns the updated AgentCore when
   * `tenantScope=false`, else TenantMind.
   */
  dismiss: (
    tenantId: string,
    agentId: string,
    proposalText: string,
    form: string,
    tenantScope = false,
  ) =>
    apiClient.post<AgentCore | TenantMind>(
      `/api/tenants/${tenantId}/agent-configs/${agentId}/core/proposals/dismiss`,
      { proposalText, form, tenantScope } satisfies ProposalActionRequest,
    ),

  // Convenience wrappers for the Core-scoped surface (observability view).
  adoptCoreProposal: (tenantId: string, agentId: string, proposalText: string, form: string) =>
    agentProposalApi.adopt(tenantId, agentId, proposalText, form, false),

  dismissCoreProposal: (tenantId: string, agentId: string, proposalText: string, form: string) =>
    agentProposalApi.dismiss(tenantId, agentId, proposalText, form, false),
};

// ── Run Learning Now (on-demand learning batch) ───────────────
//
// Normally the learning batch runs nightly. This endpoint lets an admin
// trigger it on demand for a single agent so the just-uploaded behavior /
// facts surface immediately instead of waiting for the next nightly run.
// The path carries both ids; the body is empty. The backend processes the
// agent's pending Memory→learning notes synchronously and returns the run
// counts. Behaviors land in Core as `[proposed:…]` items (review-gated);
// facts land in the Learning Bank.

/**
 * Counts returned by the on-demand learning run — ENQUEUE-level, on purpose.
 *
 * run-now hands high-priority Memory notes to LearningService, which answers 202 and
 * classifies on a detached scope, so the fact/behaviour verdict does not exist yet when
 * this response is built. The old shape carried `behaviorsProposed` / `factsToBank` /
 * `dropped` and the server returned literal zeros for all three on every run — the UI
 * showed "0 proposals · 0 to Learning Bank · 0 dropped" whether learning had worked
 * perfectly or was completely dead. Routing outcomes come from the decision trail on the
 * same page; this reports only what the operation itself observed.
 */
export interface RunLearningNowResult {
  /** High-priority notes claimed and handed to LearningService. */
  processed: number;
  /** Of those, how many LearningService accepted (2xx). */
  accepted: number;
  /** Of those, how many it refused (non-2xx) — nothing will be learned from these. */
  rejected: number;
  /** Notes whose send threw; still claimed, retried on the next run. */
  left: number;
  /** True when the agent had no high-priority notes waiting at all. */
  nothingToPromote: boolean;
}

// ── Migrate Conversations → Memory (async job) ────────────────
//
// Kicks the whole tenant's recorded conversation history through the memory
// synthesis pipeline for the SELECTED agents so their AgentMemory tiers are
// backfilled from history rather than waiting for new live turns. The POST
// returns a job id immediately (202); the GET reports running/done/failed
// progress for the polling line on the Learning tab.

/** Per-agent breakdown of a migrate-conversations run. */
export interface MigrateConversationsPerAgent {
  agentId: string;
  conversations: number;
  notesWritten: number;
}

/**
 * Why the runner passed over a session. Keys of `skippedByReason`; mirrors the backend
 * `MigrationSkipReasons` constants. An unrecognised key renders verbatim rather than
 * being dropped, so a new backend reason is visible before the UI knows its label.
 */
export type MigrateSkipReason =
  | 'unattributable'
  | 'otherAgent'
  | 'operatorTagSkip'
  | 'alreadyMined'
  | 'synthesisFailed';

/** Live status of an async migrate-conversations job. */
export interface MigrateConversationsStatus {
  status: 'running' | 'done' | 'failed';
  totalConversations: number;
  processed: number;
  notesWritten: number;
  perAgent: MigrateConversationsPerAgent[];
  /** Sessions enumerated before any filtering. */
  sessionsSeen: number;
  /** Sessions passed over, counted by reason — explains a zero instead of hiding it. */
  skippedByReason: Partial<Record<MigrateSkipReason, number>> & Record<string, number>;
}

/** 202 kickoff response — carries the job id to poll. */
export interface MigrateConversationsKickoff {
  jobId: string;
}

/**
 * OP-328 — the tenant's most-recent migrate job, used to REHYDRATE the Learning
 * tab's progress line on mount (Discovery pattern) so a browser refresh no longer
 * orphans the job. Carries `jobId` so the client can resume polling a still-`running`
 * job. The endpoint returns 204 (empty body) when the tenant has never run one.
 */
export interface LatestMigrateJob {
  jobId: string;
  status: 'running' | 'done' | 'failed';
  totalConversations: number;
  processed: number;
  notesWritten: number;
  perAgent: MigrateConversationsPerAgent[];
  agentIds: string[];
  updatedAt: string;
  sessionsSeen: number;
  skippedByReason: Partial<Record<MigrateSkipReason, number>> & Record<string, number>;
}

// ── Learning Bank info / toggle ───────────────────────────────
//
// Every agent gets a SYSTEM-MANAGED "Learning Knowledge" bank that the
// learning subsystem feeds with facts the bot learns from humans. It is
// NON-DELETABLE — the UI offers an on/off toggle only. `enabled` mirrors
// whether the Learning bankId is present in the agent's canonical
// `agentconfigs.bankIds[]`; toggling $addToSet/$pull-s the id without ever
// deleting the bank or its chunks.

/** Learning-bank summary for one agent. */
export interface LearningBankInfo {
  bankId: string;
  name: string;
  chunkCount: number;
  sourceCount: number;
  /** true when the Learning bankId is wired into the agent's `bankIds[]`. */
  enabled: boolean;
}

// ── Provision Learning banks for every bot (backfill) ─────────
//
// "Open the Learning KB for every bot" — an IDEMPOTENT tenant-wide backfill.
// A bot created before the Learning bank existed (or whose ensure-call failed)
// silently has nowhere to put learned facts, which is indistinguishable from
// "the bot learned nothing". Re-running is always safe: an agent that already
// has its bank is re-reported, not re-created.

/** One AI agent's Learning-bank provisioning outcome. */
export interface ProvisionedAgentLearningBank {
  /**
   * The agent config id. NOTE the field is `agentId`, NOT `agentConfigId` —
   * that is the pre-existing portal-facing name on this surface (it carries
   * the agentConfigId value), matching `MigrateConversationsPerAgent`.
   */
  agentId: string;
  /** Ensured Learning bank id. Empty string when provisioning failed. */
  bankId: string;
  /**
   * true when the agent ended up with a usable Learning bank. false when
   * provisioning failed OR when learning is switched OFF for the agent and no
   * cached bank exists (the provisioner deliberately skips EnsureBank — OP-085).
   */
  enabled: boolean;
  /** Why this agent has no bank, when applicable. Null on success. */
  failureReason: string | null;
}

/** Aggregate result of the provision-for-all backfill. */
export interface ProvisionLearningBanksResult {
  /** Agent configs the tenant owns (all types). */
  totalAgents: number;
  /** How many of those are AI agents — the only ones that get a Learning bank. */
  aiAgents: number;
  /** AI agents whose Learning bank was ensured successfully. */
  succeeded: number;
  /** AI agents that failed — inspect `provisioned[].failureReason`. */
  failed: number;
  /** Per-AI-agent outcome, in the order they were processed. */
  provisioned: ProvisionedAgentLearningBank[];
}

export const agentLearningApi = {
  /**
   * Trigger the learning batch on demand for one agent. No body — both ids
   * travel in the path. Resolves with the run counts; the caller refreshes
   * the Core/Memory/Feedback observability tabs so newly proposed behavior
   * is visible without a page reload.
   */
  runNow: (tenantId: string, agentId: string) =>
    apiClient.post<RunLearningNowResult>(
      `/api/tenants/${tenantId}/agent-configs/${agentId}/learning/run-now`,
      undefined,
      // OP-330 — this batch can run long on a large ticket set; the 30s
      // apiClient default aborted the client side while the run kept going
      // server-side, inviting a double-run on retry.
      { timeout: 5 * 60 * 1000 },
    ),

  /**
   * Start an async "migrate conversations → memory" job for the selected
   * agents. Returns 202 + a `jobId`; poll {@link migrateStatus} until the
   * status flips to `done` or `failed`. The endpoint is tenant-scoped (NOT
   * per-agent) because one job fans the work across every selected agent.
   *
   * Routed via `/api/ai/...` (NOT `/api/tenants/...`): these two migrate
   * endpoints are HOSTED in AiAgentService (5007), while the bank + run-now
   * URLs below are TenantMgmt. The gateway sends `/api/tenants/{**}` to the
   * tenant-mgmt-cluster and `/api/ai/{**}` to the ai-agent-cluster, so the
   * migrate calls MUST go through `/api/ai/...` to reach AiAgentService. The
   * gateway forwards the path verbatim, and the controller is mounted at the
   * matching full `api/ai/tenants/{tenantId}/learning/migrate-conversations`.
   */
  migrateConversations: (tenantId: string, agentIds: string[]) =>
    apiClient.post<MigrateConversationsKickoff>(
      `/api/ai/tenants/${tenantId}/learning/migrate-conversations`,
      { agentIds },
    ),

  /** Poll the progress of a migrate-conversations job by its id. */
  migrateStatus: (tenantId: string, jobId: string) =>
    apiClient.get<MigrateConversationsStatus>(
      `/api/ai/tenants/${tenantId}/learning/migrate-conversations/${jobId}`,
    ),

  /**
   * OP-328 — the tenant's most-recent migrate job (any status), for mount rehydrate.
   * 200 + {@link LatestMigrateJob} when one exists, 204 (empty body) when none. The
   * literal `latest` segment out-ranks the `{jobId}` route server-side, so this never
   * collides with {@link migrateStatus}.
   */
  getLatestMigrateJob: (tenantId: string) =>
    apiClient.get<LatestMigrateJob | ''>(
      `/api/ai/tenants/${tenantId}/learning/migrate-conversations/latest`,
    ),

  /**
   * Load the system-managed Learning Knowledge bank summary for one agent
   * (name + chunk/source counts + whether it's wired into `bankIds[]`).
   */
  getLearningBank: (tenantId: string, agentId: string) =>
    apiClient.get<LearningBankInfo>(
      `/api/tenants/${tenantId}/agent-configs/${agentId}/learning-bank`,
    ),

  /**
   * Toggle the Learning bank on/off for one agent. `enabled=true` $addToSet-s
   * the Learning bankId into the agent's canonical `bankIds[]`; `false`
   * $pull-s it. NEVER deletes the bank or its chunks — returns the updated
   * {@link LearningBankInfo}.
   */
  toggleLearningBank: (tenantId: string, agentId: string, enabled: boolean) =>
    apiClient.post<LearningBankInfo>(
      `/api/tenants/${tenantId}/agent-configs/${agentId}/learning-bank/toggle`,
      { enabled },
    ),

  /**
   * Ensure EVERY AI bot in the tenant has its system-managed Learning Knowledge
   * bank opened (tenant-wide backfill, hosted by LearningService and routed via
   * the plain `/api/tenants/{**}` → learning-cluster gateway rule).
   *
   * IDEMPOTENT — safe to re-run at any time; an agent that already has its bank
   * is simply re-reported. Empty body. 200 → {@link ProvisionLearningBanksResult};
   * 400 `platform_tenant_no_learning` for the reserved platform tenant (ADR-009 D1).
   *
   * Carries a longer timeout than the 30s apiClient default: the backfill walks
   * every agent config and ensures a bank per AI agent, so a tenant with many
   * bots can outlive 30s while the work keeps going server-side.
   */
  provisionBanks: (tenantId: string) =>
    apiClient.post<ProvisionLearningBanksResult>(
      `/api/tenants/${tenantId}/learning/provision-banks`,
      undefined,
      { timeout: 2 * 60 * 1000 },
    ),
};

// ── Learning activity — the decision trail ────────────────────
//
// The tenant-admin READ surface over `css_learning.classification_log`: the
// per-candidate answer to "did the bot actually learn anything, and if not,
// WHY?". This exists because the router recorded every rejection ONLY in that
// Mongo collection — with no UI onto it, a total learning outage (every
// candidate 400-ing at a middleware tenant gate) was indistinguishable from
// "there was nothing to learn", and every feature flag still read healthy.
//
// Gated on `learning.review` (same permission as the Learning page itself).

/** Source that produced a learning candidate. */
export type LearningSourceKind =
  | 'consultation'
  | 'takeover'
  | 'feedback'
  | 'conversation'
  | 'emailDraftEdit';

/** FACT-axis outcome of one classification decision (routed to the Knowledge Service). */
export interface LearningActivityFact {
  /** Whether the classifier judged the candidate a useful FACT. */
  useful: boolean;
  /** The distilled fact the classifier extracted, when any. */
  idea: string | null;
  /** Knowledge bank the fact was written to. Null when nothing was written. */
  bankId: string | null;
  /** Why the fact was NOT written, when applicable. */
  failureReason: string | null;
}

/** BEHAVIOR-axis outcome of one classification decision (routed to Agent Memory). */
export interface LearningActivityBehavior {
  /** Whether the classifier judged the candidate a BEHAVIOR instruction. */
  isBehavior: boolean;
  /** Behavior form as recorded by the router. */
  form: string | null;
  /** Behavior scope as recorded by the router. */
  scope: 'agent' | 'tenant' | null;
  /** Memory tier that received the write. */
  tier: 'TenantMind' | 'AgentCore' | null;
  /** Whether the behavior reached a memory tier. */
  memoryWritten: boolean;
  /** Whether the behavior landed as a review-gated `[proposed:{form}]` entry. */
  reviewWritten: boolean;
  /** Why the behavior was NOT written, when applicable. */
  failureReason: string | null;
}

/** Usefulness-gate outcome — the pre-write "is this worth keeping?" check. */
export interface LearningActivityUsefulnessGate {
  /** Whether the gate ran at all. Null on rows written before the gate existed. */
  ran: boolean | null;
  /** Gate verdict when it ran. */
  result: boolean | null;
  /** Gate's stated reason. */
  reason: string | null;
}

/** One classification decision, trimmed for the admin activity feed. */
export interface LearningActivityItem {
  /** Classification-log row id. */
  id: string;
  /** Agent config the decision belongs to. */
  agentConfigId: string;
  /** Originating chat session, when the candidate came from live traffic. */
  sessionId: string | null;
  /** Widened to `string` so an unknown/new server-side kind still renders. */
  sourceKind: LearningSourceKind | string;
  /** Scrubbed candidate text (already PII-scrubbed + truncated by the router). */
  inputDigest: string;
  /** When the decision was made — UTC ISO-8601. Display verbatim as UTC; never shift it. */
  classifiedAtUtc: string;
  fact: LearningActivityFact;
  behavior: LearningActivityBehavior;
  usefulnessGate: LearningActivityUsefulnessGate;
  /** Whether the dual-axis router toggle was enabled at decision time. */
  routerEnabled: boolean;
}

/** One page of the decision trail. `total` IGNORES paging, so the caller can page. */
export interface LearningActivityResponse {
  total: number;
  /** The requested page, newest decision first (`classifiedAtUtc` DESC). */
  items: LearningActivityItem[];
}

/** Query params for `GET /api/tenants/{tenantId}/learning/activity`. All optional. */
export interface LearningActivityParams {
  /** Agent-config filter. Omit / blank for every agent in the tenant. */
  agentId?: string;
  /** Source filter. Omit / blank for every source. */
  sourceKind?: string;
  /** Page size. Server clamps to its configured maximum. */
  limit?: number;
  /** Offset paging. */
  skip?: number;
}

/** Server-side default page size (`Learning.Activity.DefaultPageSize`). */
export const LEARNING_ACTIVITY_DEFAULT_LIMIT = 50;
/** Server-side hard cap (`Learning.Activity.MaxPageSize`) — asking for more is clamped. */
export const LEARNING_ACTIVITY_MAX_LIMIT = 200;

export const learningActivityApi = {
  /**
   * One page of the tenant's learning decision trail, newest first.
   *
   * Blank filters are OMITTED from the query string rather than sent as
   * `agentId=` (the backend ignores whitespace either way, but a clean URL
   * keeps the audit line readable). `limit` is clamped client-side to the same
   * bounds the server enforces so the caller's `skip` arithmetic can't drift
   * from the page it actually received.
   *
   * 200 → {@link LearningActivityResponse};
   * 400 `{ error: 'platform_tenant_no_learning' }` for the reserved platform
   * tenant; 401 / 403 / 500 carry `{ error, transactionId }`.
   */
  list: (tenantId: string, params: LearningActivityParams = {}) => {
    const query: Record<string, string | number> = {};
    if (params.agentId && params.agentId.trim()) query.agentId = params.agentId.trim();
    if (params.sourceKind && params.sourceKind.trim()) query.sourceKind = params.sourceKind.trim();
    query.limit = Math.min(
      Math.max(params.limit ?? LEARNING_ACTIVITY_DEFAULT_LIMIT, 1),
      LEARNING_ACTIVITY_MAX_LIMIT,
    );
    query.skip = Math.max(params.skip ?? 0, 0);
    return apiClient.get<LearningActivityResponse>(
      `/api/tenants/${tenantId}/learning/activity`,
      { params: query },
    );
  },
};

// ── Learning articles (2026-11-01) ────────────────────────────
//
// One documentary article per closed conversation in which a human teammate was
// consulted or took over (LearningService). Facts only. Admin approval by default;
// the tenant can switch on auto-publish, and must switch articles on at all
// (it costs AI tokens). Every route is tenant-scoped and requires `memory.manage`.

export type LearningArticleStatus = 'pending' | 'draft' | 'published' | 'dismissed' | 'skipped' | 'failed';

export interface LearningArticle {
  id: string;
  sessionId: string;
  agentConfigId: string;
  channel?: string | null;
  hadConsultation: boolean;
  hadHumanControl: boolean;
  status: LearningArticleStatus;
  skipReason?: string | null;
  title: string;
  context: string;
  facts: string[];
  pagePath?: string | null;
  pageTitle?: string | null;
  solution: string;
  missingData: string[];
  steps: string[];
  keywords: string[];
  altQuestions: string[];
  language?: string | null;
  writtenAtUtc?: string | null;
  model?: string | null;
  inputTokens: number;
  outputTokens: number;
  similarToArticleId?: string | null;
  editedBy?: string | null;
  editedAtUtc?: string | null;
  approvedBy?: string | null;
  publishedAtUtc?: string | null;
  autoPublished: boolean;
  dismissedBy?: string | null;
  dismissedAtUtc?: string | null;
  createdAt: string;
}

export interface LearningArticleList {
  total: number;
  items: LearningArticle[];
}

/** The editable fields (PUT). An omitted field is left unchanged. */
export type LearningArticleEdit = Partial<Pick<LearningArticle,
  'title' | 'context' | 'facts' | 'pagePath' | 'pageTitle' | 'solution' | 'missingData' | 'steps' | 'keywords' | 'altQuestions' | 'language'>>;

export interface LearningArticleSettings {
  articlesEnabled: boolean;
  autoPublish: boolean;
}

export const LEARNING_ARTICLES_PAGE_SIZE = 25;

export const learningArticlesApi = {
  list: (tenantId: string, params: { status?: LearningArticleStatus | ''; skip?: number; limit?: number } = {}) => {
    const query: Record<string, string | number> = {
      limit: params.limit ?? LEARNING_ARTICLES_PAGE_SIZE,
      skip: Math.max(params.skip ?? 0, 0),
    };
    if (params.status) query.status = params.status;
    return apiClient.get<LearningArticleList>(`/api/tenants/${tenantId}/learning/articles`, { params: query });
  },
  get: (tenantId: string, articleId: string) =>
    apiClient.get<LearningArticle>(`/api/tenants/${tenantId}/learning/articles/${articleId}`),
  edit: (tenantId: string, articleId: string, edit: LearningArticleEdit) =>
    apiClient.put<LearningArticle>(`/api/tenants/${tenantId}/learning/articles/${articleId}`, edit),
  approve: (tenantId: string, articleId: string) =>
    apiClient.post<LearningArticle>(`/api/tenants/${tenantId}/learning/articles/${articleId}/approve`),
  dismiss: (tenantId: string, articleId: string) =>
    apiClient.post<LearningArticle>(`/api/tenants/${tenantId}/learning/articles/${articleId}/dismiss`),
  unpublish: (tenantId: string, articleId: string) =>
    apiClient.post<LearningArticle>(`/api/tenants/${tenantId}/learning/articles/${articleId}/unpublish`),
  getSettings: (tenantId: string) =>
    apiClient.get<LearningArticleSettings>(`/api/tenants/${tenantId}/learning/article-settings`),
  saveSettings: (tenantId: string, settings: LearningArticleSettings) =>
    apiClient.put<LearningArticleSettings>(`/api/tenants/${tenantId}/learning/article-settings`, settings),
};

// ── Bot Entities (Agent + Channel) ────────────────────────────

export const botEntityApi = {
  list: (tenantId: string) =>
    apiClient.get<BotEntity[]>(`/api/tenants/${tenantId}/bots`),

  getById: (tenantId: string, id: string) =>
    apiClient.get<BotEntity>(`/api/tenants/${tenantId}/bots/${id}`),

  create: (tenantId: string, data: BotEntityFormData) =>
    apiClient.post<BotEntity>(`/api/tenants/${tenantId}/bots`, data),

  update: (tenantId: string, id: string, data: Partial<BotEntityFormData>) =>
    apiClient.put<BotEntity>(`/api/tenants/${tenantId}/bots/${id}`, data),

  delete: (tenantId: string, id: string) =>
    apiClient.delete(`/api/tenants/${tenantId}/bots/${id}`),

  activate: (tenantId: string, id: string) =>
    apiClient.post<BotEntity>(`/api/tenants/${tenantId}/bots/${id}/activate`),

  deactivate: (tenantId: string, id: string) =>
    apiClient.post<BotEntity>(`/api/tenants/${tenantId}/bots/${id}/deactivate`),
};

// ── Knowledge Banks (Knowledge Ingestion v2) ──────────────────

import type {
  KnowledgeBank,
  CreateKnowledgeBankRequest,
  UpdateKnowledgeBankRequest,
  RefinePurposeRequest,
  RefinePurposeResponse,
  DiscoveryRun,
  DiscoveryLink,
  DiscoveryDefaults,
  StartDiscoveryRequest,
  PatchDiscoveryLinkRequest,
  IndexSelectedResponse,
  BankChunk,
  UpsertChunkRequest,
  BankOperationKickoff,
  BankOperationStatus,
  MediaItem,
  MediaActivationQueued,
} from '@/types';

export const knowledgeBankApi = {
  list: (tenantId: string) =>
    apiClient.get<KnowledgeBank[]>(`/api/tenants/${tenantId}/knowledge-banks`),
  getById: (tenantId: string, bankId: string) =>
    apiClient.get<KnowledgeBank>(`/api/tenants/${tenantId}/knowledge-banks/${bankId}`),
  create: (tenantId: string, data: CreateKnowledgeBankRequest) =>
    apiClient.post<KnowledgeBank>(`/api/tenants/${tenantId}/knowledge-banks`, data),
  update: (tenantId: string, bankId: string, data: UpdateKnowledgeBankRequest) =>
    apiClient.put<KnowledgeBank>(`/api/tenants/${tenantId}/knowledge-banks/${bankId}`, data),
  refinePurpose: (tenantId: string, bankId: string, data: RefinePurposeRequest) =>
    apiClient.post<RefinePurposeResponse>(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/refine-purpose`,
      data,
    ),
  delete: (tenantId: string, bankId: string) =>
    apiClient.delete(`/api/tenants/${tenantId}/knowledge-banks/${bankId}`),
  /**
   * Re-read pages. With no `sourceIds` this is the bank-wide re-parse the
   * "Re-read pages" button has always sent — the body is omitted entirely so
   * that request stays byte-identical on the wire. Passing ids re-indexes only
   * that selection (Sources tab multi-select).
   *
   * URL sources only, either way: re-parsing re-fetches a page, and an uploaded
   * file's bytes are discarded after extraction unless
   * `Knowledge.Media.KeepOriginals` was on when it was uploaded (ships false).
   */
  reparse: (tenantId: string, bankId: string, sourceIds?: string[]) =>
    apiClient.post<BankOperationKickoff>(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/reparse`,
      sourceIds && sourceIds.length > 0 ? { sourceIds } : undefined,
    ),
  /**
   * Live progress of the most recent re-parse / re-embed operation on a
   * bank. Returns 404 when no operation has run in the last ~2 minutes.
   * Polled every ~2 s by the admin portal while a progress bar is
   * visible; soft-fails (auto-isolated from the global axios 401/logout
   * branch via `_retry`) so a stale 404 / network blip never bounces
   * the operator to /login.
   */
  operationStatus: async (tenantId: string, bankId: string): Promise<BankOperationStatus | null> => {
    try {
      const { data } = await apiClient.get<BankOperationStatus>(
        `/api/tenants/${tenantId}/knowledge-banks/${bankId}/operation-status`,
        { _retry: true } as any,
      );
      return data;
    } catch (e: any) {
      // 404 = "no recent op" — that's normal, not an error to surface.
      // OP-303: everything else (401/5xx/network) used to be swallowed into
      // the same null too, so a real outage looked identical to "idle" and
      // the poller never stopped or backed off. Let usePoller's
      // consecutive-error cutoff see real failures.
      if (e?.response?.status === 404) return null;
      throw e;
    }
  },
  /**
   * Re-embed every chunk in the bank with the currently configured embedding
   * model. Use after swapping `AiAgent.Embedding.Model` — vector spaces are
   * not portable across models, so old chunks become useless. Cheaper than
   * `reparse` (no URL re-fetch, no LLM idea-extraction; only embedding HTTP
   * calls). Idempotent.
   */
  reembed: (tenantId: string, bankId: string) =>
    apiClient.post<BankOperationKickoff>(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/re-embed`,
    ),
  /**
   * Bank-scoped knowledge sources (KB v2). Mirrors the per-agent
   * `/agent-configs/{agentId}/knowledge` surface but scopes by `bankId`.
   * The same extract→chunk→embed pipeline runs server-side; chunks are
   * stamped with `bankId` instead of `agentConfigId`.
   */
  listSources: (tenantId: string, bankId: string) =>
    apiClient.get<KnowledgeSource[]>(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/sources`,
    ),
  uploadFile: (tenantId: string, bankId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post<KnowledgeSource>(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/files`,
      fd,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        // Same 5-minute cap as the agent-side surface. Server returns
        // 201 in <1 s with status=Processing now (fire-and-forget
        // pipeline); we only need to outlast the bytes-on-the-wire.
        timeout: 5 * 60 * 1000,
      },
    );
  },
  removeSource: (tenantId: string, bankId: string, sourceId: string) =>
    apiClient.delete(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/sources/${sourceId}`,
    ),
  setSourceEnabled: (
    tenantId: string,
    bankId: string,
    sourceId: string,
    enabled: boolean,
  ) =>
    apiClient.patch<KnowledgeSource>(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/sources/${sourceId}/enabled`,
      { enabled },
    ),
};

export const discoveryApi = {
  start: (tenantId: string, bankId: string, data: StartDiscoveryRequest) =>
    apiClient.post<DiscoveryRun>(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/discoveries`,
      data,
    ),
  /** List discovery runs for a bank, newest-first. UI uses this on tab
   *  mount to rehydrate the most-recent run so results don't disappear. */
  list: (tenantId: string, bankId: string, limit = 10) =>
    apiClient.get<DiscoveryRun[]>(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/discoveries`,
      { params: { limit } },
    ),
  getById: (tenantId: string, bankId: string, runId: string) =>
    apiClient.get<DiscoveryRun>(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/discoveries/${runId}`,
    ),
  /** Tenant-resolved form defaults (default relevance cutoff). Config-driven
   *  so the UI's starting cutoff isn't hardcoded (P4 improvement 5). */
  getDefaults: (tenantId: string, bankId: string) =>
    apiClient.get<DiscoveryDefaults>(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/discoveries/defaults`,
    ),
  patchLink: (
    tenantId: string,
    bankId: string,
    runId: string,
    linkId: string,
    data: PatchDiscoveryLinkRequest,
  ) =>
    apiClient.patch<DiscoveryLink>(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/discoveries/${runId}/links/${linkId}`,
      data,
    ),
  indexSelected: (tenantId: string, bankId: string, runId: string) =>
    apiClient.post<IndexSelectedResponse>(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/discoveries/${runId}/index`,
    ),
  /** Bulk-delete a set of links in one round-trip. Server marks each as
   *  deleted=true + selected=false. Deleted links are excluded from
   *  cross-run dedup, so a future discovery can re-grade them fresh. */
  bulkDelete: (tenantId: string, bankId: string, runId: string, linkIds: string[]) =>
    apiClient.post<DiscoveryRun>(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/discoveries/${runId}/links/bulk-delete`,
      { linkIds },
    ),
  /** Re-grade selected links in-place: server re-fetches + re-runs the
   *  grading SLM against the bank's current refined-purpose. Selected /
   *  Deleted state is preserved. Fire-and-forget — returns the pre-regrade
   *  run immediately; the caller polls `getById` for the updated grades
   *  (OP-364: `knowledgeBankApi.operationStatus` now also reports live
   *  per-batch progress while this is running). */
  regrade: (tenantId: string, bankId: string, runId: string, linkIds: string[]) =>
    apiClient.post<DiscoveryRun>(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/discoveries/${runId}/regrade`,
      { linkIds },
    ),
};

// ── Ticket Imports (cold-start historical-ticket ingestion) ────
//
// Multipart upload endpoints mirror the per-agent knowledge surface — the
// admin POSTs raw `.json` / `.jsonl` exports, the backend stores them in
// GridFS, creates a ledger row, and publishes a MassTransit event. The UI
// polls the `list` endpoint every 3 s while any row is non-terminal.

import type {
  HistoricalTicketImport,
  ImportedTicket,
  TicketImportMapping,
} from '@/types/ticketImport';

export const ticketImportApi = {
  /** List ticket-import jobs for an agent, newest-first. Used by the polling
   *  table on the Import panel. */
  list: (tenantId: string, agentId: string) =>
    apiClient.get<HistoricalTicketImport[]>(
      // Relocated to CrmIntegrationService 2026-05-31 — routed via the gateway's
      // /api/crm/{**catch-all} (no path transform, full path forwarded to 5009).
      `/api/crm/tenants/${tenantId}/agent-configs/${agentId}/ticket-imports`,
    ),

  /** Upload one or more `.json` / `.jsonl` ticket-export files as a single
   *  batch. Imported tickets always land in the bot's system-managed
   *  "Learning Bank" — there is no operator bank choice. The DPA
   *  confirmation gate is enforced server-side — a falsey flag yields 400.
   *  Returns one ledger row per uploaded file. */
  upload: (
    tenantId: string,
    agentId: string,
    files: File[],
    dpaConfirmed: boolean,
  ) => {
    const fd = new FormData();
    for (const f of files) fd.append('files', f);
    // Multipart form fields are always strings on the wire; the backend
    // parses them into bool via the framework's standard binders.
    fd.append('dpaConfirmed', dpaConfirmed ? 'true' : 'false');
    return apiClient.post<HistoricalTicketImport[]>(
      `/api/crm/tenants/${tenantId}/agent-configs/${agentId}/ticket-imports`,
      fd,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        // 200 MB-per-file cap server-side; allow time for the bytes to
        // travel even on a slow uplink. The backend returns 202 before
        // any actual ingestion happens, so we only need to outlast the
        // upload phase itself.
        timeout: 10 * 60 * 1000,
      },
    );
  },

  /** Preview-parse a sample file WITHOUT persisting anything. Returns up to
   *  10 canonical `ImportedTicket` rows resolved with the tenant's current
   *  saved mapping. Used for ad-hoc mapping validation. */
  preview: (tenantId: string, agentId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post<ImportedTicket[]>(
      `/api/crm/tenants/${tenantId}/agent-configs/${agentId}/ticket-imports/preview`,
      fd,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 2 * 60 * 1000,
      },
    );
  },
};

export const ticketImportMappingApi = {
  /** Load the tenant's mapping. The backend always returns a default-shaped
   *  row when none has been persisted, so a 404 is never seen on this path. */
  get: (tenantId: string) =>
    apiClient.get<TicketImportMapping>(
      `/api/crm/tenants/${tenantId}/ticket-import-mapping`,
    ),

  /** Atomic-replace upsert of the tenant's mapping. */
  upsert: (tenantId: string, body: TicketImportMapping) =>
    apiClient.put<TicketImportMapping>(
      `/api/crm/tenants/${tenantId}/ticket-import-mapping`,
      body,
    ),
};

export const bankChunkApi = {
  list: (tenantId: string, bankId: string) =>
    apiClient.get<BankChunk[]>(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/chunks`,
    ),
  create: (tenantId: string, bankId: string, data: UpsertChunkRequest) =>
    apiClient.post<BankChunk>(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/chunks`,
      data,
    ),
  update: (tenantId: string, bankId: string, chunkId: string, data: UpsertChunkRequest) =>
    apiClient.put<BankChunk>(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/chunks/${chunkId}`,
      data,
    ),
  delete: (tenantId: string, bankId: string, chunkId: string) =>
    apiClient.delete(`/api/tenants/${tenantId}/knowledge-banks/${bankId}/chunks/${chunkId}`),
};

// ── Bank Media (K-5) — per-source KnowledgeMedia CRUD for the gallery ────
// Backend: KnowledgeBankController's `.../sources/{sourceId}/media` group.
// Every route is source-scoped (matches IBankMediaService's own contract) —
// Mutations are per-source because a media row is owned by (tenant, bank, source);
// READS come in both shapes — one source, or the whole bank with each row labelled
// by the source it came from.
export const bankMediaApi = {
  list: (tenantId: string, bankId: string, sourceId: string) =>
    apiClient.get<MediaItem[]>(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/sources/${sourceId}/media`,
    ),
  /** Every picture in the bank, each carrying its owning source's display name. */
  listForBank: (tenantId: string, bankId: string) =>
    apiClient.get<MediaItem[]>(`/api/tenants/${tenantId}/knowledge-banks/${bankId}/media`),
  /**
   * SUPPRESSES the image: it leaves the gallery and stops being retrievable, but the row and its
   * bytes are kept so it can be reviewed under the Deleted filter and restored in one click. The
   * server remembers its content hash so re-indexing the source will not bring it back — without
   * that, dedup (which keys on hash, before storage) would match nothing and every re-crawl would
   * restore exactly what was just removed.
   */
  delete: (tenantId: string, bankId: string, sourceId: string, mediaId: string) =>
    apiClient.delete(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/sources/${sourceId}/media/${mediaId}`,
    ),
  /** Un-deletes: back to `pending`, so the next Activate describes and indexes it again. */
  restore: (tenantId: string, bankId: string, sourceId: string, mediaId: string) =>
    apiClient.post<MediaItem>(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/sources/${sourceId}/media/${mediaId}/restore`,
    ),
  /** Frees a deleted image's bytes for good. A hash-only tombstone stays so it never returns. */
  purge: (tenantId: string, bankId: string, sourceId: string, mediaId: string) =>
    apiClient.delete(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/sources/${sourceId}/media/${mediaId}/purge`,
    ),
  /**
   * Phase 2: QUEUES description of every `pending` image in the bank. Answers 202 with the number
   * queued — the work then runs in the background, and progress is visible in the media list as
   * pending rows flip to active.
   *
   * Not synchronous: 175 images at ~4s of vision each is about twelve minutes, and holding the
   * request open for that both times out AND starves the gallery's polling into the gateway rate
   * limiter. 409 `images_disabled` when ingestion is off for the tenant — distinct from a zero
   * queue, which means nothing was waiting.
   */
  activate: (tenantId: string, bankId: string) =>
    apiClient.post<MediaActivationQueued>(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/media/activate`,
    ),
  setPrimary: (tenantId: string, bankId: string, sourceId: string, mediaId: string) =>
    apiClient.patch<MediaItem>(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/sources/${sourceId}/media/${mediaId}/primary`,
    ),
  setCaption: (tenantId: string, bankId: string, sourceId: string, mediaId: string, caption: string) =>
    apiClient.patch<MediaItem>(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/sources/${sourceId}/media/${mediaId}/caption`,
      { caption },
    ),
  /**
   * Finding C / S-6 — tri-state per-image customer-shareability override. `shareable` is
   * `null` (inherit from source/bank), `true` (release to customers), or `false` (hold back) —
   * always send one of the three explicitly; there is no "leave unchanged" shape here.
   */
  setShareable: (tenantId: string, bankId: string, sourceId: string, mediaId: string, shareable: boolean | null) =>
    apiClient.patch<MediaItem>(
      `/api/tenants/${tenantId}/knowledge-banks/${bankId}/sources/${sourceId}/media/${mediaId}/shareable`,
      { shareable },
    ),
};

// ── Session learning tags ─────────────────────────────────────
//
// Operators can tag a session in one of three states:
//   "skip"         — do NOT synthesize/learn this session
//   "force-learn"  — synthesize even if low-signal/anonymous
//   "default"      — normal gate behaviour (effectively removes the tag)
//
// A free-text note travels alongside the tag; the memory synthesizer prepends
// it to the synthesized material as a human directive when present.
//
// Stored at: Session.Metadata["learning-tag"] + Session.Metadata["learning-note"]
// Backend service: SessionManagementService (port 5005).

/** Possible values for a session learning tag. */
export type LearningTag = 'skip' | 'force-learn' | 'default';

/**
 * A session row returned by GET /api/sessions/learning-tagged.
 * The list contains only sessions whose learning-tag is NOT "default"
 * (i.e. sessions explicitly queued for force-learn or explicitly skipped).
 * Ordered newest-first by lastActivityAt.
 */
export interface TaggedSession {
  /** Session id. (Backend field: LearningTaggedSessionResponse.SessionId.) */
  sessionId: string;
  /** Tenant id. */
  tenantId: string;
  /** The active learning tag. */
  tag: LearningTag;
  /** Optional human-authored note that will be prepended to synthesized material. */
  note: string | null;
  /** Id of the customer on this session. */
  customerId: string | null;
  /** Id of the human agent assigned to this session (if any). */
  assignedAgentId: string | null;
  /** ISO-8601 timestamp of the last message / activity on this session. */
  lastActivityAt: string | null;
}

// ── Core management (reclassify + purge) ─────────────────────
//
// Two operator-facing maintenance operations on the machine-curated Core
// tier for a given agent.
//
// Reclassify: runs every plain Core note (not already [proposed:…]) through
// the dual-axis Learning router so mis-classified notes get re-routed to the
// correct destination (Learning Bank fact vs [proposed:behavior]).
//
// Purge: deletes Core notes matching a source-prefix and/or added-before
// cutoff. MUST be called twice by the UI:
//   1. dryRun:true  → returns matched + sampleMatched (preview)
//   2. dryRun:false → returns deleted + remaining (apply)
//
// Backend: TenantManagementService (5018), routes on
//   POST /api/tenants/{tenantId}/agent-configs/{agentId}/core/reclassify
//   POST /api/tenants/{tenantId}/agent-configs/{agentId}/core/purge

/** Counts returned by the reclassify operation. camelCase mirrors the
 *  ASP.NET Core JSON serialiser output of `CoreReclassifyResult`. */
export interface CoreReclassifyResult {
  /** Total plain Core notes that were run through the router. */
  processed: number;
  /** Notes removed from Core (re-routed to Learning Bank or discarded). */
  removed: number;
  /** Notes re-added to Core as [proposed:…] behavior proposals. */
  proposalsCreated: number;
  /** Notes written to the Learning Bank as facts. */
  factsWritten: number;
  /** Notes already prefixed [proposed:…] — skipped without processing. */
  skipped: number;
  /** Notes left in Core because the router threw on them (partial failure). */
  left: number;
}

/** Request body for the purge endpoint. */
export interface CorePurgeRequest {
  /** Filter by source prefix, e.g. "post-turn:" or "session-close:".
   *  Omit (undefined) to match any source. */
  sourcePrefix?: string;
  /** ISO-8601 date string; only notes added BEFORE this date are matched.
   *  Omit to match any age. */
  addedBefore?: string;
  /** true = dry run (preview only, no deletions). */
  dryRun: boolean;
}

/** Response from the purge endpoint (both dry-run and apply modes). */
export interface CorePurgeResult {
  tenantId: string;
  agentId: string;
  /** Notes that match the filter (dry-run: what WOULD be deleted; apply: what WAS). */
  matched: number;
  /** Notes actually deleted (0 on a dry run). */
  deleted: number;
  /** Notes remaining in Core after the operation. */
  remaining: number;
  /** Whether this response is from a dry-run call. */
  dryRun: boolean;
  /** Sample of up to 10 matched note texts, for preview display. */
  sampleMatched: string[];
}

export const coreManagementApi = {
  /**
   * Re-run every plain Core note through the dual-axis Learning router.
   * Notes already prefixed `[proposed:…]` are skipped.
   * Returns reclassification counts.
   */
  reclassify: (tenantId: string, agentId: string) =>
    apiClient.post<CoreReclassifyResult>(
      `/api/tenants/${tenantId}/agent-configs/${agentId}/core/reclassify`,
      undefined,
      // OP-330 — re-running every Core note through the Learning router can
      // take a while on a large bank; avoid the 30s apiClient default
      // aborting the client side while the server keeps reclassifying
      // (inviting a double-run on retry).
      { timeout: 5 * 60 * 1000 },
    ),

  /**
   * Preview (dryRun:true) or apply (dryRun:false) a Core purge.
   * Must call with dryRun:true before dryRun:false — the UI enforces
   * this by disabling Apply until a successful preview exists.
   */
  purge: (tenantId: string, agentId: string, body: CorePurgeRequest) =>
    apiClient.post<CorePurgeResult>(
      `/api/tenants/${tenantId}/agent-configs/${agentId}/core/purge`,
      body,
    ),
};

export const sessionLearningApi = {
  /**
   * List sessions that carry a non-default learning tag for the caller's
   * tenant (scoped via X-Tenant-Id header, added by the Axios interceptor).
   *
   * Backend route: GET /api/sessions/learning-tagged
   * Service: SessionManagementService (5005)
   * Auth: [Authorize] — JWT must carry tenantId claim.
   *
   * @param limit Maximum rows to return (default 100, as per the contract).
   */
  listTagged: (limit = 100) =>
    apiClient.get<TaggedSession[]>('/api/sessions/learning-tagged', {
      params: { limit },
    }),

  /**
   * Set / reverse / clear a session's learning disposition.
   * POST /api/sessions/{sessionId}/learning-tag  { tag, note? }
   *
   * The same endpoint the agent console uses; exposed here so an admin can
   * re-tag or un-tag ("reverse") a session straight from the Learning tab.
   * Passing tag='default' clears the tag (and any note).
   *
   * Auth: [Authorize]; tenant-scoped via the X-Tenant-Id header.
   */
  setTag: (sessionId: string, tag: LearningTag, note?: string) =>
    apiClient.post<unknown>(`/api/sessions/${sessionId}/learning-tag`, {
      tag,
      ...(note ? { note } : {}),
    }),
};

// ── CRM Integration (R12, ADR-006) ────────────────────────────────────────────
//
// All routes are served by CrmIntegrationService (port 5009), forwarded by the
// gateway via `/api/crm/{**catch-all}` (no path transform; full path forwarded).
//
// TOKEN SECURITY: the HubSpot Private App token NEVER travels through
// CrmIntegrationService routes. It is written ONCE via ConfigurationManagementService's
// admin create-entry endpoint (POST /api/config/entries) with IsEncrypted=true.
// Key: CrmIntegration.HubSpot.PrivateAppToken, Scope: service_tenant,
// ServiceName: CrmIntegrationService. The token value NEVER appears in any GET
// response and is NEVER sent back to the frontend.

import type {
  CrmTenantSettings,
  CrmTenantSettingsFormData,
  CrmSchemaDiscovery,
  CrmTestConnectionResult,
  CrmConnectionStatus,
  CrmExportLedgerPage,
  CrmExportLedgerRow,
  CrmSessionContext,
} from '@/types';

export const crmSettingsApi = {
  /**
   * Load the tenant's CRM settings (provider, portalMode, stageMap, contextProperties,
   * exportEnabled). 404 → treat as "not yet configured" — render empty form defaults.
   *
   * Backend route: GET /api/crm/tenants/{tenantId}/settings
   * Service: CrmIntegrationService (5009)
   */
  get: (tenantId: string) =>
    apiClient.get<CrmTenantSettings>(`/api/crm/tenants/${tenantId}/settings`),

  /**
   * Upsert the tenant's CRM settings. Token is NOT accepted in this body;
   * see {@link crmConfigEntryApi.writeToken} for the token write path.
   *
   * Backend route: PUT /api/crm/tenants/{tenantId}/settings
   * Service: CrmIntegrationService (5009)
   */
  put: (tenantId: string, data: CrmTenantSettingsFormData) =>
    apiClient.put<CrmTenantSettings>(`/api/crm/tenants/${tenantId}/settings`, data),
};

// ── CRM pipelines (WS-4 — stage-picker source of truth) ──────────────────────
//
// A small, tenant-scoped read used by the ConversationStates + CrmSettings stage
// pickers. Distinct from `crmSchemaApi` (the heavier discovery/refresh surface):
// this is the canonical, cache-friendly "what pipelines/stages exist" lookup that
// a parallel backend stream owns. Tenant is claim-bound server-side (resolved from
// the JWT via the axios X-Tenant-Id interceptor), so the path carries no tenantId.
//
// Contract: `{ provider, simulated, pipelines: [ { id, label, stages: [ { id,
// label, displayOrder } ] } ] }`. Callers MUST degrade gracefully: on 404/error,
// or when `simulated === true` with no usable pipelines, fall back to a free-text
// "pipelineId::stageId" entry so the form is never blocked.

/** One stage inside a CRM pipeline (GET /api/crm/pipelines). */
export interface CrmPipelineStageOption {
  id: string;
  label: string;
  displayOrder?: number;
  /** True when the portal marks this stage as closing the ticket (OP-1485; sent on every stage). */
  isClosed?: boolean;
}

/** One CRM pipeline with its ordered stages (GET /api/crm/pipelines). */
export interface CrmPipelineOption {
  id: string;
  label: string;
  stages: CrmPipelineStageOption[];
}

/** Shape of `GET /api/crm/pipelines`. */
export interface CrmPipelinesResponse {
  provider: string;
  /** true when the CRM provider is the Stub simulator (no real pipelines). */
  simulated: boolean;
  pipelines: CrmPipelineOption[];
}

export const crmPipelinesApi = {
  /**
   * List the tenant's CRM pipelines + stages for the stage pickers. Tenant is
   * resolved server-side from the JWT claim (axios interceptor supplies the
   * X-Tenant-Id header). 404 / error / simulated-with-no-data → callers fall
   * back to free-text stage entry.
   *
   * `_retry: true` isolates this optional lookup from the global 401
   * refresh-then-logout branch, so a missing/unrouted endpoint can never bounce
   * an admin to /login (mirrors pipelineConfigApi / operationStatus).
   */
  get: () =>
    apiClient.get<CrmPipelinesResponse>('/api/crm/pipelines', { _retry: true } as any),
};

// ── ADR-017 — per-tenant conversation states (categories) ─────────────
/** Locale-keyed display label. Every state ships en + he (ADR-017 D6). */
export interface LocalizedLabel {
  en: string;
  he: string;
}

/** A per-tenant conversation-state definition (SessionManagementService `conversation_state_defs`). */
export interface ConversationStateDef {
  id: string;
  tenantId: string;
  key: string;
  label: LocalizedLabel;
  color: string;
  order: number;
  /** One of NEW | ACTIVE | CONSULTING | HUMAN_CONTROLLED | CLOSED — advisory. */
  mapsToLifecycle: string;
  /** "pipelineId::stageId" for outbound HubSpot sync, or null = console-only. */
  crmStageRef: string | null;
  isDefault: boolean;
  /** ADR-017 phase 2 auto-categorisation trigger binding, or null = manual-only. */
  autoTrigger: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Create/update body. On create `key` is required + tenant-unique; immutable after. */
export interface ConversationStateDefFormData {
  key: string;
  label: LocalizedLabel;
  color: string;
  order: number;
  mapsToLifecycle: string;
  crmStageRef: string | null;
  isDefault: boolean;
  /** One of the CategoryAutoTriggers slugs, or null = manual-only. Unique per active def. */
  autoTrigger: string | null;
  isActive: boolean;
}

/** One CRM pipeline stage as POST /api/sessions/state-defs/import-stages takes it (PR-A). */
export interface ImportStageItem {
  id: string;
  label: string;
  displayOrder: number;
  isClosed: boolean;
}

/** Body of POST /api/sessions/state-defs/import-stages. */
export interface ImportStagesRequest {
  pipelineId: string;
  pipelineLabel?: string;
  stages: ImportStageItem[];
}

/** Answer of POST /api/sessions/state-defs/import-stages: `matched` = stages imported before (idempotent re-run). */
export interface ImportStagesResult {
  created: number;
  matched: number;
  total: number;
}

/**
 * Per-tenant conversation-state definitions (ADR-017 D1) - "Conversation Statuses" on screen. Tenant is claim-bound
 * server-side (the axios `X-Tenant-Id` interceptor selects the effective tenant), so no tenantId in these paths.
 * PR-A: GET no longer seeds defaults - a tenant with none gets []; DELETE answers 409 `state_in_use` while any
 * conversation uses the status (deactivate it instead); `importStages` adds one status per CRM pipeline stage
 * (idempotent; 409 `too_many_statuses` past 100 active statuses).
 *
 * Backend routes: /api/sessions/state-defs — SessionManagementService.
 */
export const conversationStatesApi = {
  list: () => apiClient.get<ConversationStateDef[]>('/api/sessions/state-defs'),
  create: (data: ConversationStateDefFormData) =>
    apiClient.post<ConversationStateDef>('/api/sessions/state-defs', data),
  update: (id: string, data: ConversationStateDefFormData) =>
    apiClient.put<ConversationStateDef>(`/api/sessions/state-defs/${id}`, data),
  remove: (id: string) => apiClient.delete<void>(`/api/sessions/state-defs/${id}`),
  importStages: (body: ImportStagesRequest) =>
    apiClient.post<ImportStagesResult>('/api/sessions/state-defs/import-stages', body),
};

// ── PR-A — Agent Console settings (SessionManagementService) ─────────────
/**
 * GET/PUT /api/sessions/console-settings. `privateConversations` is the tenant's STORED choice (null = inherit the
 * platform default); `privateConversationsEffective` is what the server enforces now - the only value a control may
 * show as its state.
 */
export interface ConsoleSettings {
  tenantId: string;
  privateConversations: boolean | null;
  privateConversationsEffective: boolean;
  privateConversationsInherited: boolean;
  hideTagFolders: boolean;
  /** Every agent may change the tags, status, subject and CRM ticket properties of a CLOSED conversation (default false). */
  editClosedConversations: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
}

/** PUT body - a FULL replace: always send every field (privateConversations null = inherit the platform default). */
export interface ConsoleSettingsUpdate {
  privateConversations: boolean | null;
  hideTagFolders: boolean;
  /** Optional on the wire (an older body is not a reset), but this page always sends it. */
  editClosedConversations?: boolean;
}

export const consoleSettingsApi = {
  /** Any staff with `workspace.view`. Tenant is claim-bound (the X-Tenant-Id interceptor picks the effective tenant). */
  get: () => apiClient.get<ConsoleSettings>('/api/sessions/console-settings'),
  /** `config.edit`. 400 on a bad body, 403 without the permission. Answers the same shape as GET. */
  put: (body: ConsoleSettingsUpdate) => apiClient.put<ConsoleSettings>('/api/sessions/console-settings', body),
};

export const crmSchemaApi = {
  /**
   * Load the cached schema/discovery result for a tenant (pipelines + property catalog).
   * Returns 404 when no discovery has run yet.
   *
   * Backend route: GET /api/crm/tenants/{tenantId}/schema/discovery
   * Service: CrmIntegrationService (5009)
   */
  get: (tenantId: string) =>
    apiClient.get<CrmSchemaDiscovery>(`/api/crm/tenants/${tenantId}/schema/discovery`),

  /**
   * Trigger a fresh schema discovery from HubSpot (re-fetches pipelines + property
   * catalog via the adapter and caches the result). The response is the updated schema.
   *
   * Backend route: POST /api/crm/tenants/{tenantId}/schema/discovery/refresh
   * Service: CrmIntegrationService (5009)
   */
  refresh: (tenantId: string) =>
    apiClient.post<CrmSchemaDiscovery>(
      `/api/crm/tenants/${tenantId}/schema/discovery/refresh`,
    ),
};

export const crmTestConnectionApi = {
  /**
   * Test the currently configured HubSpot token. Returns portalId + scopes on
   * success, or an errorMessage on failure. Never throws on CRM error — the
   * backend wraps adapter exceptions into a failure response (fail-soft).
   *
   * Backend route: POST /api/crm/tenants/{tenantId}/test-connection
   * Service: CrmIntegrationService (5009)
   */
  test: (tenantId: string) =>
    apiClient.post<CrmTestConnectionResult>(
      `/api/crm/tenants/${tenantId}/test-connection`,
    ),
};

export const crmConnectionStatusApi = {
  /**
   * The CRM tab's connection card: which CRM serves the tenant, whether the HubSpot
   * token is usable (state + last four characters only — never the token), the
   * read/write switches, and the last Test Connection. Makes no call to the CRM.
   *
   * Backend route: GET /api/crm/tenants/{tenantId}/connection-status
   * Service: CrmIntegrationService (5009)
   */
  get: (tenantId: string) =>
    apiClient.get<CrmConnectionStatus>(`/api/crm/tenants/${tenantId}/connection-status`),
};

export const crmExportApi = {
  /**
   * List export ledger rows for a tenant with optional status filter and paging.
   * Rows are ordered newest-first.
   *
   * Backend route: GET /api/crm/tenants/{tenantId}/exports?status=&page=&pageSize=
   * Service: CrmIntegrationService (5009)
   */
  list: (
    tenantId: string,
    status?: string,
    page = 1,
    pageSize = 20,
  ) =>
    apiClient.get<CrmExportLedgerPage>(
      `/api/crm/tenants/${tenantId}/exports`,
      { params: { ...(status ? { status } : {}), page, pageSize } },
    ),

  /**
   * Retry a dead export: resets status to pending + clears attempts, republishes
   * CrmExportRequested. Returns the updated ledger row.
   *
   * Backend route: POST /api/crm/tenants/{tenantId}/exports/{sessionId}/retry
   * Service: CrmIntegrationService (5009)
   */
  retry: (tenantId: string, sessionId: string) =>
    apiClient.post<CrmExportLedgerRow>(
      `/api/crm/tenants/${tenantId}/exports/${sessionId}/retry`,
    ),

  /**
   * Load the cached CRM context for a session (from crm_context_caches).
   * Used by the agent console; exposed here so CrmSettingsPage can show a
   * preview when debugging context issues.
   *
   * Backend route: GET /api/crm/tenants/{tenantId}/sessions/{sessionId}/context
   * Service: CrmIntegrationService (5009)
   */
  getContext: (tenantId: string, sessionId: string) =>
    apiClient.get<CrmSessionContext>(
      `/api/crm/tenants/${tenantId}/sessions/${sessionId}/context`,
    ),
};

/**
 * Write the HubSpot Private App token to ConfigurationManagementService with
 * AES-256 encryption. The token NEVER goes to CrmIntegrationService and is
 * NEVER echoed back. This is the ONLY path for token writes.
 *
 * Backend route: POST /api/config/entries
 * Service: ConfigurationManagementService (5019)
 * Key:   CrmIntegration.HubSpot.PrivateAppToken
 * Scope: service_tenant  (ServiceName=CrmIntegrationService, TenantId=<caller>)
 * IsEncrypted: true
 */
export const crmConfigEntryApi = {
  writeToken: (tenantId: string, tokenValue: string) =>
    apiClient.post<RawConfigEntry>('/api/config/entries', {
      key: 'CrmIntegration.HubSpot.PrivateAppToken',
      value: tokenValue,
      scope: 'service_tenant',
      serviceName: 'CrmIntegrationService',
      tenantId,
      description: 'HubSpot Private App token — written encrypted, never echoed.',
      isEncrypted: true,
    } satisfies {
      key: string;
      value: string;
      scope: string;
      serviceName: string;
      tenantId: string;
      description: string;
      isEncrypted: boolean;
    }),
};

// ── Tenant Settings — curated, sectioned config (WS3 Feature 1) ──────────
//
// A tenant-admin-facing surface, distinct from the platformAdmin-only raw
// `configApi` above: ConfigurationManagementService groups a curated subset
// of tenant-overridable keys into named sections (conversations, takeover,
// notifications, media, localization, privacy) with enough metadata
// (valueType + isDefault + optional min/max) for a generic, metadata-driven
// editor — see `TenantSettingsEditor.tsx`. Routed through the gateway's
// existing `config-mgmt-route` (`/api/config/{**catch-all}`); no new gateway
// route is needed.
//
// Backend routes (ConfigurationManagementService, 5019):
//   GET    /api/config/tenant-settings/{tenantId}
//   PUT    /api/config/tenant-settings/{tenantId}          { key, value }
//   DELETE /api/config/tenant-settings/{tenantId}/{key}    (reset to default)

/** How a tenant-setting item's `value` string should be rendered/edited. */
export type TenantSettingValueType = 'bool' | 'int' | 'string';

/** One curated, tenant-overridable config item. `value` always travels as a
 *  string on the wire (e.g. "true"/"false" for `bool`, "24" for `int`) —
 *  the editor parses/formats per `valueType`. */
export interface TenantSettingItem {
  key: string;
  valueType: TenantSettingValueType;
  value: string;
  /** true = currently resolving to the system default (no tenant override row). */
  isDefault: boolean;
  /** Only meaningful when `valueType === 'int'`. */
  minValue?: number;
  /** Only meaningful when `valueType === 'int'`. */
  maxValue?: number;
}

/** A named group of related {@link TenantSettingItem}s (e.g. "conversations"). */
export interface TenantSettingsSection {
  sectionKey: string;
  items: TenantSettingItem[];
}

/** Shape of `GET /api/config/tenant-settings/{tenantId}`. */
export interface TenantSettingsResponse {
  sections: TenantSettingsSection[];
}

export const tenantSettingsApi = {
  /** Load every curated section + item for the tenant, with resolved values. */
  get: (tenantId: string) =>
    apiClient.get<TenantSettingsResponse>(`/api/config/tenant-settings/${tenantId}`),

  /** Upsert a single tenant-scoped override. 400 on an invalid key/value. */
  update: (tenantId: string, key: string, value: string) =>
    apiClient.put<void>(`/api/config/tenant-settings/${tenantId}`, { key, value }),

  /** Remove the tenant's override for `key`, reverting it to the system default. */
  reset: (tenantId: string, key: string) =>
    apiClient.delete<void>(
      `/api/config/tenant-settings/${tenantId}/${encodeURIComponent(key)}`,
    ),
};

// ── Notification Preferences — per-user (WS3 Feature 2, "My Settings") ───
//
// Every authenticated role (agent/seniorAgent/admin/platformAdmin) manages
// their OWN notification channels on the My Settings page. `agentId` is the
// logged-in user's own id (`AuthUser.userId`) — never another user's.
//
// Backend routes (NotificationService):
//   GET /api/notifications/{agentId}/preferences
//   PUT /api/notifications/{agentId}/preferences   (full 7-boolean body)

/** The seven independently-toggleable notification channels/categories. */
export interface NotificationPreferences {
  emailEnabled: boolean;
  pushEnabled: boolean;
  inAppEnabled: boolean;
  slaWarningEnabled: boolean;
  slaBreachEnabled: boolean;
  escalationEnabled: boolean;
  systemEnabled: boolean;
}

/** Shape of `GET /api/notifications/{agentId}/preferences`. */
export interface NotificationPreferencesResponse extends NotificationPreferences {
  agentId: string;
}

export const notificationApi = {
  /** Load the caller's own notification preferences. */
  getPreferences: (agentId: string) =>
    apiClient.get<NotificationPreferencesResponse>(
      `/api/notifications/${agentId}/preferences`,
    ),

  /** Replace all seven preferences in one call. */
  updatePreferences: (agentId: string, prefs: NotificationPreferences) =>
    apiClient.put<NotificationPreferencesResponse>(
      `/api/notifications/${agentId}/preferences`,
      prefs,
    ),
};

// ── Memory-tier note CRUD (Bot's Mind console) ────────────────
//
// Note-level mutations across the three note-bearing memory tiers: Tenant
// Mind (tenant-wide), Agent Core (per bot, machine-curated) and Agent Memory
// (per bot, per end customer). Hosted by AgentMemoryService (5021) and
// gateway-routed; every route is tenant-scoped and requires `memory.manage`.
//
// IMPORTANT — notes have NO id. A MindNote is identified by its EXACT
// (`text`, `addedAt`) pair, which is what every edit/delete body carries.
// A stale edit/delete (the note changed server-side since the page loaded)
// returns **404 with error code `note_not_found`**; callers must treat that
// as "refetch and tell the user", not as a hard error. See
// `isNoteNotFound` in `src/components/bot-mind/notes.ts`.
//
// All verbs are POST — including edit/delete — because the note key travels
// in the body (a note's text can be arbitrarily long / contain slashes, so it
// can never be a path segment).
//
// Every call returns the FULL updated tier document, so the caller can either
// take the response or refetch; the Bot's Mind console refetches so the
// proposal/plain split is always recomputed from server truth.

/** Body of the append/add routes. */
export interface AppendNoteRequest {
  /** The new note's text. Server stamps source/priority/addedAt. */
  text: string;
}

/** Body of the `…/notes/edit` routes. `text` + `addedAt` locate the note. */
export interface EditNoteRequest {
  text: string;
  addedAt: string;
  newText: string;
}

/** Body of the `…/notes/delete` routes. `text` + `addedAt` locate the note. */
export interface DeleteNoteRequest {
  text: string;
  addedAt: string;
}

export const memoryNotesApi = {
  // ── Tier 1: Tenant Mind (tenant-wide, not per bot) ──────────
  appendTenantMindNote: (tenantId: string, text: string) =>
    apiClient.post<TenantMind>(
      `/api/tenants/${tenantId}/mind/notes`,
      { text } satisfies AppendNoteRequest,
    ),

  editTenantMindNote: (tenantId: string, text: string, addedAt: string, newText: string) =>
    apiClient.post<TenantMind>(
      `/api/tenants/${tenantId}/mind/notes/edit`,
      { text, addedAt, newText } satisfies EditNoteRequest,
    ),

  deleteTenantMindNote: (tenantId: string, text: string, addedAt: string) =>
    apiClient.post<TenantMind>(
      `/api/tenants/${tenantId}/mind/notes/delete`,
      { text, addedAt } satisfies DeleteNoteRequest,
    ),

  // ── Tier 3: Agent Core (per bot, machine-curated) ───────────
  appendCoreNote: (tenantId: string, agentId: string, text: string) =>
    apiClient.post<AgentCore>(
      `/api/tenants/${tenantId}/agent-configs/${agentId}/core/notes`,
      { text } satisfies AppendNoteRequest,
    ),

  editCoreNote: (
    tenantId: string,
    agentId: string,
    text: string,
    addedAt: string,
    newText: string,
  ) =>
    apiClient.post<AgentCore>(
      `/api/tenants/${tenantId}/agent-configs/${agentId}/core/notes/edit`,
      { text, addedAt, newText } satisfies EditNoteRequest,
    ),

  deleteCoreNote: (tenantId: string, agentId: string, text: string, addedAt: string) =>
    apiClient.post<AgentCore>(
      `/api/tenants/${tenantId}/agent-configs/${agentId}/core/notes/delete`,
      { text, addedAt } satisfies DeleteNoteRequest,
    ),

  // ── Tier 4: Agent Memory (per bot, per end customer) ────────
  // `userId` is an opaque per-channel identity (`cust-{guid:N}` for anonymous
  // widget customers) — encoded because it is a path segment here.
  addMemoryNote: (tenantId: string, agentId: string, userId: string, text: string) =>
    apiClient.post<AgentMemory>(
      `/api/tenants/${tenantId}/agent-configs/${agentId}/memory/${encodeURIComponent(userId)}/notes/add`,
      { text } satisfies AppendNoteRequest,
    ),

  editMemoryNote: (
    tenantId: string,
    agentId: string,
    userId: string,
    text: string,
    addedAt: string,
    newText: string,
  ) =>
    apiClient.post<AgentMemory>(
      `/api/tenants/${tenantId}/agent-configs/${agentId}/memory/${encodeURIComponent(userId)}/notes/edit`,
      { text, addedAt, newText } satisfies EditNoteRequest,
    ),

  deleteMemoryNote: (
    tenantId: string,
    agentId: string,
    userId: string,
    text: string,
    addedAt: string,
  ) =>
    apiClient.post<AgentMemory>(
      `/api/tenants/${tenantId}/agent-configs/${agentId}/memory/${encodeURIComponent(userId)}/notes/delete`,
      { text, addedAt } satisfies DeleteNoteRequest,
    ),
};

// ── Attachments (MediaAttachmentService) — WS-E.4 ──────────────
//
// Compact attachment rendering in the staff workspace conversation panels
// (mirrors agent-console). GET bytes for a single attachment by id — used
// for inline image-thumbnail preview and non-image chip download. Not
// deployed yet on a parallel stream — callers (MessageAttachments) must
// degrade to a plain chip on failure rather than throw.

export const mediaApi = {
  contentUrl: (fileId: string) => `/api/media/${encodeURIComponent(fileId)}/content`,
};

export async function fetchAttachmentBlob(
  fileId: string,
  plane: 'conversation' | 'knowledge' = 'conversation',
): Promise<Blob> {
  // 2026-09-05 - two planes, two owning services, two routes. A knowledge-plane id is a content hash
  // served by KnowledgeBaseService (the staff route below, not gated on the customer-facing serve
  // switch); a conversation-plane id is a media id served by MediaAttachmentService. Before this the
  // workspace always hit /api/media and every bank image the bot sent rendered as a broken chip.
  const url = plane === 'knowledge'
    ? `/api/knowledge/images/${encodeURIComponent(fileId)}/staff-content`
    : mediaApi.contentUrl(fileId);
  const response = await apiClient.get<Blob>(url, {
    responseType: 'blob',
  });
  return response.data;
}

// ── Knowledge images (K-5 gallery) — same authed-blob pattern as
// fetchAttachmentBlob above. KnowledgeImageController serves bytes only to a
// caller holding a valid staff/customer JWT (Authorize, no anonymous byte
// route), so a bare image tag can never work here — there is nowhere on it
// to put a bearer token. Every caller fetches via apiClient (which
// attaches Authorization + X-Tenant-Id) and turns the response into an
// object URL.
//
// Finding D (2026-08-15) — admin-portal is a STAFF-ONLY app, so it must use
// ONLY the staff curation route below: gated on `Permissions.KnowledgeManage`
// + tenant scope, NEVER on `Knowledge.Images.ServeEnabled`. This file used to
// also export a `contentUrl`/`fetchKnowledgeImageBlob` pair for the CUSTOMER
// route (gated by ServeEnabled, which ships false) — nothing in admin-portal
// imported them, but their mere presence was the trap: the next
// preview/lightbox component would have autocompleted the shorter, more
// obvious `fetchKnowledgeImageBlob` name, every thumbnail would 404 while
// ServeEnabled is off, and the "fix" would have been flipping the exact
// switch that lets the bot start sending those same images to customers —
// reintroducing this finding from inside the file that removed it. Removed
// outright rather than kept-but-renamed: the customer route belongs to the
// customer-facing channel surfaces (chat-widget/AiAgent), not this app: grep
// confirms nothing here imports `knowledgeImageApi` or
// `fetchKnowledgeImageStaffBlob` from any path but the one below.

export const knowledgeImageApi = {
  /** Staff curation route — the ONLY one admin-portal may call. See the finding-D note above for
   *  why the customer-facing `/content` route (gated by ServeEnabled) has no helper here at all. */
  staffContentUrl: (sha256: string) => `/api/knowledge/images/${encodeURIComponent(sha256)}/staff-content`,
};

/**
 * Concurrency gate for gallery thumbnails.
 *
 * WHY THIS EXISTS. Every MediaCard fetches its own bytes from its own effect, so a bank with 175
 * images issued 175 simultaneous GETs the moment the tab mounted. The gateway's global per-IP
 * limiter is a 600/min fixed window with QueueLimit 0 (ApiGatewayService/Program.cs:338-344), and
 * `::1` gets no exemption — so two or three gallery mounts inside one minute exhausted the window
 * for the WHOLE portal. On 2026-08-22 that produced 3,700 rejected thumbnail requests in 90
 * minutes and, in the same window, three rejected `POST .../media/activate` calls: the admin
 * pressed "Index kept images" and got "Indexing could not start." because their own gallery had
 * spent the budget. The fix is not a bigger budget; it is not spending it all at once.
 *
 * Six sits just under a browser's own per-host HTTP/1.1 cap, so the queue — rather than the socket
 * pool — is what orders the work, and that ordering stays visible from the network panel.
 *
 * Deliberately a plain FIFO with no cancellation: a card that unmounts while queued still completes
 * its fetch and the caller's own `cancelled` flag discards the result. Threading an AbortSignal
 * through would save bytes on a fast scroll, but the response is cacheable now and the next mount
 * reuses it, so the saving is one-time while the lifetime bugs would be permanent.
 */
const THUMBNAIL_CONCURRENCY = 6;
let thumbnailsInFlight = 0;
const thumbnailQueue: Array<() => void> = [];

function acquireThumbnailSlot(): Promise<void> {
  if (thumbnailsInFlight < THUMBNAIL_CONCURRENCY) {
    thumbnailsInFlight += 1;
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    thumbnailQueue.push(() => {
      thumbnailsInFlight += 1;
      resolve();
    });
  });
}

function releaseThumbnailSlot(): void {
  thumbnailsInFlight -= 1;
  const next = thumbnailQueue.shift();
  if (next) next();
}

/** The staff curation fetch MediaTab uses for its gallery thumbnails. */
export async function fetchKnowledgeImageStaffBlob(sha256: string): Promise<Blob> {
  await acquireThumbnailSlot();
  try {
    const response = await apiClient.get<Blob>(knowledgeImageApi.staffContentUrl(sha256), {
      responseType: 'blob',
    });
    return response.data;
  } finally {
    // In the `finally`, so a rejected fetch cannot strand a slot. Leaking one slot per failure
    // would narrow the gate to zero over a session and freeze the gallery — and the symptom would
    // be "thumbnails stopped loading" with nothing in the network panel at all, because the
    // requests would never be issued.
    releaseThumbnailSlot();
  }
}
